/* eslint-disable no-param-reassign, max-lines, no-return-await */
import { isFileTypeSupported, logUnsupportedFileTypeEvent } from "common/file-type-helper";
import getVariantInfo from "common/jiffy/get-variant";
import { redirectTo } from "common/utils/browser";
import { deleteCookie } from "common/utils/cookie";
import { objNamesToCamelCase } from "common/utils/helpers/cases";
import { findIndexById } from "common/utils/helpers/find-index-by-id";
import { logUploadError } from "common/utils/honeybadger/log-upload-error";
import { tryParse } from "common/utils/json";
import {
  getErrorContext,
  logUploadErrorEvent
} from "common/utils/mixpanel/dropzone-error-event-tracker";
import { submitCompletedEvent, submitDtfEvent } from "common/utils/mixpanel/submit-dtf-events";
import mobileApp from "common/utils/mobile/app";
import { DTF_PROGRESS_MAPPING } from "common/utils/progress-bar/constants";
import prometheusTracker from "common/utils/prometheus-tracker";
import {
  trackRudderstackImageProcessingCompleted,
  trackRudderstackImageRemoved,
  trackRudderstackImageUploadRetry,
  trackRudderstackImageUploadStarted
} from "common/utils/rudderstack/tracker";
import { getStickerDimensions } from "common/utils/stickers";
import { DEFAULT_STICKER_VALUES } from "common/utils/stickers/constants";
import idleCallback from "common/utils/ui/idle_callback";
import { incrementHeaderCounter } from "components/jiffy/masthead-cart/masthead-cart";
import {
  deliveryDateResponse,
  reloadDeliveryEstimates
} from "components/jiffy/product-details-delivery-date-simple/init-dates";
import validationErrors from "components/jiffy/product-details-quantity-form/show-product-modules/validation-errors";
import {
  calculateDefaultDimensions,
  calculateDimensions,
  convertImageToHexColor,
  getFormPayload,
  getIsBuyItAgainStatus,
  getPdpImage,
  getRandomId,
  groupByKeyPrefix,
  recalculateCustomSizes,
  validateGangSheetDimensions,
  zipArray,
  zipArrayByKey
} from "components/jiffy/vue-product-details-dtf/utils";
import { sendDataLayer } from "jiffy-analytics";
import { api } from "jiffy-api";
import { EventBus, Events } from "jiffy-store";
import debounce from "lodash.debounce";
import { defineStore } from "pinia";
import {
  BACKEND_TRIGGERED_ENHANCEMENT_ELIGIBLE_CATALOG,
  DEFAULT_PUFF_VALUES,
  DEFAULT_VALUES,
  GANG_DEFAULT_VALUES,
  GOLD_FOIL_HEX,
  JERSEY_NUMBERS_DEFAULT_VALUES,
  LETTER_DEFAULTS,
  LETTERS_FILL_IN_VALUES,
  MIXPANEL_METADATA_KEYS,
  RETRY_INTERVALS
} from "./common/constants";
import initCable from "./dtf-channel"; // eslint-disable-line import/no-cycle
import { trackAddToCart } from "./dtf-ga-tracker";
import ErrorTypes from "./error-types";
import { SPANGLE_MODES, useSpanglesStore } from "./spangles-module"; // eslint-disable-line import/no-cycle
import transfersPollingService from "./transfers-polling-service";

const DEFAULT_SIZE_AND_QUANTITY = {
  ...DEFAULT_VALUES,
  preset: "custom",
  id: getRandomId()
};

const DEFAULT_SIMULATION_ARTWORK = {
  ...LETTER_DEFAULTS,
  widthEdited: false,
  isSimulated: true,
  id: "simulation",
  style: null,
  height: null,
  color: null,
  quantity: 1,
  quantities: {},
  sizes: {},
  prices: {},
  enhancementNote: "",
  enhancementFeedbackSubmitted: false,
  enhancementRatingSubmitted: false,
  enhancementSatisfied: null,
  sizesAndQuantities: [DEFAULT_SIZE_AND_QUANTITY],
  enhancementTier: "full_enhancements"
};

const initialState = {
  showModal: false,
  showArtworkHistoryModal: false,
  variantId: null,
  productId: null,
  catalogNumber: null,
  printAsIs: false,
  neckLabel: false,
  dtfMaxWidth: null,
  dtfMaxHeight: null,
  dtfMinWidth: null,
  dtfMinHeight: null,
  pdpType: null,
  preCut: false,
  productSlug: "",
  showCTA: false,
  favorites: [],
  sticker: null,
  currentStore: null,
  allowForceBgRemove: false,
  canvaReviewer: null,
  unitPrice: null,
  printingServiceName: null,
  cachedArtworkPrices: {},
  dtfCategory: null,
  spangles: [],
  canvaDtfEnabledState: false,
  showMultiSizeArtwork: false,
  enablePreCut: false,
  preCutPrice: 0,
  preCutEnabled: false,
  preCutDuration: null,
  preCutUpsellThreshold: 0,
  loadFromConfig: true,
  artworksState: [],
  artworkHistory: [],
  artworkHistoryPagination: {
    page: 1
  },
  /**
   * @type {import('./types').PriceSummary}
   */
  summary: {
    orderTotal: "0.0",
    productCartQuantity: 0
  },
  /**
   * BOGO promotion data from the pricing API
   * @type {{ applicable: boolean, freeQtyAvailable?: number, artworks?: object }}
   */
  bogoPromotionData: {
    applicable: false
  },
  /**
   * @type {import('./types').Standard.Artwork | import('./types').Sticker.Artwork}
   */
  simulation: { ...DEFAULT_SIMULATION_ARTWORK },
  fileToRemove: null,
  showDiscardModal: false,
  discarded: null,
  fetchController: null,
  useDefaultValues: false,
  showFilesError: false,
  replaceArtwork: false,
  errorTypes: ErrorTypes,
  isRetrying: false,
  allRetryFailed: false,
  locale: "en-US",
  experiments: {},
  minStickerQuantity: 10,
  userType: "guest_user",
  dtfWorkerEnhancement: false,
  dtfBackendTriggeredEnhancement: false,
  transferPdpBackButtonDetection: false,
  spangleConfig: {
    spangleAlgorithms: [],
    isSpangle: false,
    spangleColors: []
  },
  sizeErrorTypes: [
    ErrorTypes.INVALID_WIDTH.toString(),
    ErrorTypes.INVALID_HEIGHT.toString(),
    ErrorTypes.NEGATIVE_VALUES.toString(),
    ErrorTypes.MIN_PRINT_SIZE.toString(),
    ErrorTypes.MAX_PRINT_SIZE.toString(),
    ErrorTypes.UPSCALE_THRESHOLD_EXCEEDED.toString()
  ],
  isMobile: false,
  enableMobileFooterCTA: false,
  dtfChannelUuid: null,
  isEnhancementLoading: false,
  actionCableOptions: {},
  actionCableConnected: false,
  stickerQrScanned: false,
  cable: null,
  businessLine: null,
  isPuff: false,
  puff: { ...DEFAULT_PUFF_VALUES },
  isProColor: false,
  isSticker: false,
  isStickerSample: false,
  isJiffyMade: false,
  printReadyInfoConfig: {
    show: false,
    expanded: false
  },
  loading: {
    removeBg: false
  },
  isJerseyNumbers: false,
  isLetters: false,
  letterPreviewMessages: {},
  artworkBackground: "transparent",
  backgroundType: "checkered",
  stickersStyleOptions: false,
  letterPreviewLoading: false,
  isGoldFoil: false,
  forcePdpTransfersRedraw: false,
  enableRedraw: false,
  isPricingLoading: false,
  enhancementNote: "",
  puffMaxImageCount: 1,
  previewCanvasSize: { width: 0, height: 0 },
  gangSheetBuilder: null,
  showGangSheetBuilder: false,
  generatedGangSheets: [],
  gangSheetBuilderShopId: null,
  showGangSheetBuilderFeature: false,
  isGangSheetBuilder: false,
  gangSheetBuilderVariants: [],
  gangSheetBuilderSettings: {},
  gangSheetBuilderSizes: [],
  isDtfSheet: false,
  gangSheetBulkPrice: {},
  storeCreditInfo: {},
  presetMockupColor: ["#E1E1E1"],
  gangSheetBuilderCarousels: {},
  updatePricingAbortSignal: null,
  preloadedDesignId: null,
  reorderEnabled: false,
  validateArtworkStartTime: {},
  validateArtworkStartEventIntervalID: {},
  paidExpertEditAddonEnabled: false,
  gangSheetCatalogNumbers: [],
  customerReviewOnRedraws: false,
  customerReviewOnTransferRedraws: false,
  klaviyoSmsTransactionalSubscribed: false,
  smsTransactionalSubscriptionPdpEnabled: false,
  transferPdpInactivityDetection: false,
  smsTransactionalPhone: "",
  smsTransactionalSubscribeButtonText: "Subscribe",
  messagingConsents: { smsTransactional: true },
  blankItemsCount: 0,
  transfersInCartCount: 0,
  isNewToTransfers: null
};

const emptyArtworkItem = {
  id: null,
  file: null,
  name: null,
  isFile: true,
  metadata: null,
  borneoMetadata: null,
  isLoading: true,
  removeBackground: false,
  width: null,
  height: null,
  quantity: null,
  pricing: {},
  isValid: true,
  projectId: null,
  uploadError: null,
  toggleBackgroundError: null,
  originalWidth: null,
  originalHeight: null,
  progress: 0,
  retryFailed: false,
  currentProgress: 0,
  lastWebSocketEvent: null,
  lastWebSocketEventData: {},
  spangled: false,
  spangledImageUrl: null,
  cutlineUrl: null,
  imageProcessingCompletedEventSent: false,
  color: null,
  paddingSize: "no_padding",
  bgColor: null,
  data: {},
  printReadyInfo: {},
  processTimeStamp: null,
  annotateEnabled: false,
  annotateMode: "keep",
  annotatedPoints: [],
  annotatedCanvasWidth: null,
  annotatedCanvasHeight: null,
  annotatedXOffset: 0,
  annotatedYOffset: 0,
  revisionImageUrls: [],
  revisionPosition: 0,
  sizesAndQuantities: [DEFAULT_SIZE_AND_QUANTITY],
  enhancedNoBgUrl: null,
  enhancedWithBgUrl: null,
  enhancementCompleted: false,
  enhancementFeedbackSubmitted: false,
  enhancementRatingSubmitted: false,
  originalDpi: 0,
  sizeBasedDpi: 0,
  enhancedDpi: 0,
  sliderPreviewUrl: null,
  downscaledOriginalImage: null,
  catalogNumber: null,
  fromGangSheetBuilder: false,
  enhancementTier: "full_enhancements"
};

const summarizeFonts = fontDetails =>
  fontDetails.reduce(
    (acc, font) => {
      if (font.font_family) {
        acc.fontFamilies.push(font.font_family);
      }
      if (font.text_effect?.name) {
        acc.textEffects.push(font.text_effect.name);
      }
      return acc;
    },
    { fontFamilies: [], textEffects: [] }
  );

const validation = validationErrors();

// Helper function to map size and quantity data
function mapSizeAndQuantity({ width, height, quantity, preset, size }) {
  return { width, height, quantity, preset, size };
}

// Helper function to transform artwork for form submission
function transformArtworkToFormData(artwork, store) {
  const sizes = artwork.sizesAndQuantities.filter(({ quantity }) => Number(quantity) > 0);
  const llmRedrawRequestId =
    ["custom", "full_enhancements"].includes(artwork.enhancementTier) && artwork.llmRedrawRequestId
      ? artwork.llmRedrawRequestId
      : null;

  return {
    artwork_id: artwork.id,
    width: artwork.width,
    height: artwork.height,
    quantity: store.showMultiSize ? 1 : Number(artwork.quantity),
    from_dam: artwork.fromDam,
    variant_id: artwork.variantId || store.variantId,
    metadata: artwork.metadata,
    borneo_metadata: artwork.borneoMetadata,
    project_id: artwork.projectId,
    spangled_image_url: artwork.spangledImageUrl,
    spangled: artwork.spangled,
    spangle_csv: artwork.spangleCsv,
    spangle_pages_info: artwork.spanglePagesInfo,
    spangle_positions_json: artwork.spanglePositionsJson,
    color: artwork.color,
    padding_size: artwork.paddingSize,
    preview_url: artwork.url || artwork.originalNoBgUrl,
    bg_color: artwork.bgColor,
    shape: artwork.shape,
    remove_background: artwork.removeBackground,
    enhancement_request_note: artwork.enhancementNote,
    llm_redraw_request_id: llmRedrawRequestId,
    enhancement_tier: artwork.enhancementTier,
    enhance_image: artwork.enhanceImage,
    from_gang_sheet_builder: artwork.fromGangSheetBuilder,
    catalog_number: artwork.catalogNumber,
    sizes_and_quantities: sizes.map(mapSizeAndQuantity),
    messaging_consents: store.messagingConsents || { smsTransactional: true }
  };
}

export const useDtfStore = defineStore("dtf", {
  state() {
    return { ...initialState };
  },
  getters: {
    showSampleAddToCart() {
      const urlParams = new URLSearchParams(window.location.search);

      return (
        urlParams.get("spt") === "true" &&
        this.catalogNumber === "DTF001" &&
        this.simulation.unitPrice > 0
      );
    },
    isGuest() {
      return this.userType === "guest_user";
    },
    /**
     * Returns BOGO-adjusted pricing data for a specific artwork/size
     * @returns {function(string, string, string): {freeQty: number, discountedTotalPrice: number} | null}
     */
    getBogoAdjustedPrice() {
      return (artworkId, sizeId, sizeKey) => {
        if (!this.bogoPromotionData?.applicable || !this.bogoPromotionData?.artworks) {
          return null;
        }

        const artworkData = this.bogoPromotionData.artworks[artworkId];
        if (!artworkData) return null;

        // Normalize sizeId: objNamesToCamelCase converts UUIDs with dashes
        // e.g., "411cb8e6-6b64-4b16-9ec5-b4b1c3d12606" becomes "411cb8e66b644b169ec5B4b1c3d12606"
        const normalizedSizeId = sizeId.replace(/[^a-zA-Z0-9]+(.)/g, (m, chr) => chr.toUpperCase());
        const sizeData = artworkData[normalizedSizeId];
        if (!sizeData) return null;

        // Normalize sizeKey: ensure 2 decimal places to match backend format
        const [width, height] = sizeKey.split("x");
        const normalizedSizeKey = `${parseFloat(width).toFixed(2)}x${parseFloat(height).toFixed(2)}`;

        return sizeData[normalizedSizeKey] || null;
      };
    },
    /**
     * Returns total free quantity available from BOGO promotion
     * @returns {number}
     */
    bogoFreeQtyAvailable() {
      return this.bogoPromotionData?.freeQtyAvailable || 0;
    },
    /**
     * Returns whether BOGO promotion is applicable
     * @returns {boolean}
     */
    isBogoApplicable() {
      return this.bogoPromotionData?.applicable || false;
    },
    /**
     * @returns {Array<import('./types').Standard.Artwork | import('./types').Sticker.Artwork>}
     */
    artworks() {
      return this.artworksState.length ? this.artworksState : [this.simulation];
    },
    modalArtworks() {
      return this.artworks;
    },
    isFavorite() {
      return this.favorites.includes(this.productId);
    },
    withPreCut() {
      return this.preCutEnabled && this.preCutPrice > 0;
    },
    files() {
      return this.artworks.filter(({ isFile }) => isFile);
    },
    loadedArtworks() {
      return this.artworks.filter(({ isFile }) => !isFile);
    },
    canvaDtfEnabled() {
      return this.canvaDtfEnabledState && this.catalogNumber === "DTF001";
    },
    mobileFooterCTAEnabled() {
      return this.isMobile && this.enableMobileFooterCTA;
    },
    jerseyFormArtworks() {
      const artwork = this.letterNumberArtwork;
      return [
        {
          style: artwork.style,
          height: artwork.height,
          width: artwork.width,
          color: artwork.color,
          quantities: artwork.quantities,
          quantity: artwork.quantity,
          metadata: {}
        }
      ];
    },
    letterFormArtworks() {
      const artwork = this.letterNumberArtwork;
      const texts = artwork.texts.filter(Boolean);
      return [
        {
          texts,
          font: artwork.font,
          style: artwork.style,
          height: artwork.height,
          width: artwork.width,
          color: artwork.color,
          quantity: texts.length,
          max_length: artwork.maxLength,
          metadata: {}
        }
      ];
    },
    formArtworks() {
      if (this.isJerseyNumbers) {
        return this.jerseyFormArtworks;
      }

      if (this.isLetters) {
        return this.letterFormArtworks;
      }

      return this.artworksState.map(artwork => transformArtworkToFormData(artwork, this));
    },
    letterNumberArtworks() {
      const { quantities, ...artwork } = this.letterNumberArtwork;
      const image = getPdpImage();
      if (this.isJerseyNumbers) {
        return Object.keys(quantities).map(key => ({
          ...artwork,
          id: key,
          url: image,
          pricing: { unitPrice: artwork.prices[key]?.unitPrice },
          number: key,
          height: artwork.sizes[key]?.height,
          width: artwork.sizes[key]?.width,
          quantity: quantities[key],
          preCut: this.preCut
        }));
      }

      const texts = artwork.texts.filter(Boolean);
      return texts.map((text, index) => ({
        ...artwork,
        id: index + 1,
        url: image,
        pricing: { unitPrice: artwork.prices[index + 1]?.unitPrice },
        text,
        height: artwork.sizes[index]?.height,
        width: artwork.sizes[index]?.width,
        quantity: 1,
        preCut: this.preCut
      }));
    },
    /* eslint-disable max-lines-per-function */
    csmData() {
      const artworks =
        this.isJerseyNumbers || this.isLetters ? this.letterNumberArtworks : this.artworksState;
      const result = artworks.map(artwork => {
        let sizeText = `${artwork.width}" x ${artwork.height}"`;
        if (this.isJerseyNumbers) {
          sizeText = `Height ${artwork.height}"`;
        } else if (this.isLetters) {
          sizeText = `${Number(artwork.width).toFixed(2)}" x ${Number(artwork.height).toFixed(2)}"`;
        } else if (this.printAsIs) {
          sizeText = `${artwork.width}" x ${artwork.lengthFoot}'`;
        }

        const currency = this.locale === "en-CA" ? "C$" : "$";

        const catalogText = (() => {
          if (this.sticker) {
            return "Jiffy Stickers";
          }

          if (this.spangleConfig.isSpangle) {
            return "Jiffy Spangles";
          }

          if (this.isPuff) {
            return "Puff Transfers";
          }

          if (this.isJerseyNumbers) {
            return `Numbers (#${artwork.number})`;
          }

          if (this.isLetters) {
            return `Custom Names ("${artwork.text.toUpperCase()}")`;
          }

          return "Jiffy Transfers";
        })();

        return {
          id: artwork.id,
          dieCutProductId: artwork.dieCutProductId,
          dieCutVariantId: artwork.dieCutVariantId,
          catalogNumber: catalogText,
          productId: this.productId,
          variantId: this.variantId,
          color: "",
          image: artwork.url,
          size: sizeText,
          quantity: artwork.quantity,
          cutlineUrl: artwork.cutlineUrl,
          price: `${currency}${Number(artwork.pricing.unitPrice).toFixed(2)}`,
          preCut: this.preCut,
          preCutPrice: this.preCutPrice
        };
      }, []);

      // Add enhancement services (same as csmDataMultiSize)
      if (this.summary.enhancements) {
        const currency = this.locale === "en-CA" ? "C$" : "$";
        const isFaceliftExperimentActive =
          this.experiments.ab_transfers_pdp_facelift_october_2025 === 1;

        Object.values(this.summary.enhancements).forEach(enhancement => {
          // Only show print_original enhancements when experiment is active
          const isPrintOriginalService = enhancement.serviceName === "Print As Is Notes";
          if (isPrintOriginalService && !isFaceliftExperimentActive) {
            return;
          }

          result.push({
            catalogNumber: enhancement.serviceName,
            priceTotal: `${currency}${Number(enhancement.totalPrice).toFixed(2)}`,
            quantity: enhancement.quantity,
            isEnhancement: true
          });
        });
      }

      return result;
    },
    /* eslint-enable max-lines-per-function */
    csmDataMultiSize() {
      const csmData = [];
      const currency = this.locale === "en-CA" ? "C$" : "$";

      this.artworksState.forEach(({ id, sizesAndQuantities, url }) => {
        sizesAndQuantities
          .filter(({ quantity }) => Number(quantity) > 0)
          .forEach(item => {
            csmData.push({
              id,
              catalogNumber: "Jiffy Transfers",
              productId: this.productId,
              variantId: this.variantId,
              color: "",
              image: url,
              size: `${item.width}" x ${item.height}${this.printAsIs ? "'" : '"'}`,
              quantity: item.quantity,
              price: `${currency}${Number(item.unitPrice).toFixed(2)}`,
              preCut: this.preCut,
              preCutPrice: this.preCutPrice
            });
          });
      });

      if (this.summary.enhancements) {
        const isFaceliftExperimentActive =
          this.experiments.ab_transfers_pdp_facelift_october_2025 === 1;

        Object.values(this.summary.enhancements).forEach(enhancement => {
          // Only show print_original enhancements when experiment is active
          const isPrintOriginalService = enhancement.serviceName === "Print As Is Notes";
          if (isPrintOriginalService && !isFaceliftExperimentActive) {
            return;
          }

          csmData.push({
            catalogNumber: enhancement.serviceName,
            priceTotal: `${currency}${Number(enhancement.totalPrice).toFixed(2)}`,
            quantity: enhancement.quantity
          });
        });
      }
      return csmData;
    },
    totalQuantity() {
      if (this.isJerseyNumbers || this.isLetters) {
        return this.letterNumberArtwork.quantity;
      }

      if (this.showMultiSize) {
        return this.artworksState.reduce(
          (artworkTotal, { sizesAndQuantities }) =>
            artworkTotal +
            sizesAndQuantities.reduce(
              (sizesTotal, { quantity }) => sizesTotal + Number(quantity),
              0
            ),
          0
        );
      }

      return this.artworksState.reduce((acc, { quantity }) => acc + Number(quantity), 0);
    },
    outOfStock() {
      if (this.isGangSheetBuilder) return false;

      const available = window.inventory[this.variantId];
      return available == null || available === 0;
    },
    dtfTypeDropText() {
      return this.printAsIs ? "Drop your gang sheets here" : "Drop your images here";
    },
    isLetterReadyForOrder() {
      const { style, font, height, maxLength, color, sizes, ...artwork } = this.letterNumberArtwork;
      const texts = artwork.texts.filter(Boolean);
      const quantity = texts.length;
      const allSizesDefined = texts.every((_text, index) => {
        const size = sizes[index];
        return size && size.width !== undefined && size.height !== undefined;
      });

      return (
        style &&
        font &&
        height &&
        maxLength &&
        color &&
        quantity &&
        artwork.isValid &&
        allSizesDefined
      );
    },
    // eslint-disable-next-line max-lines-per-function
    isReadyForOrder() {
      if (this.isJerseyNumbers) {
        const { quantity, quantities, height, ...artwork } = this.letterNumberArtwork;
        return (
          quantity &&
          height &&
          artwork.isValid &&
          Object.keys(quantities).every(key => artwork.prices[key]?.unitPrice)
        );
      }

      if (this.isLetters) {
        return this.isLetterReadyForOrder;
      }

      return (
        Boolean(this.artworksState.length) &&
        this.artworksState.every(this.isArtworkValid) &&
        !this.isPricingLoading
      );
    },
    dpiInstructionsUrl() {
      return this.locale === "en-CA" ? "/ca/artwork-dpi-instructions" : "/artwork-dpi-instructions";
    },
    gangSheetArtworkProductLink() {
      let baseLink;
      if (this.catalogNumber === "DTFGANG001") {
        baseLink = "/jiffytransfers-DTF001.html";
      } else if (this.catalogNumber === "UVDTFGANG") {
        baseLink = "/jiffytransfers-UVDTF01.html";
      } else if (this.catalogNumber === "GANGDTFPC1") {
        baseLink = "/jiffytransfers-DTFPC1.html";
      } else if (this.catalogNumber === "GANGDTFG01") {
        baseLink = "/jiffytransfers-DTFG01.html";
      } else {
        baseLink = "/jiffytransfers-UVDTF01.html";
      }
      return this.locale === "en-CA" ? `/ca${baseLink}` : baseLink;
    },
    spangleAlgorithms() {
      return this.spangleConfig.spangleAlgorithms;
    },
    isSpangle() {
      return this.spangleConfig.isSpangle;
    },
    spangleColors() {
      return this.spangleConfig.spangleColors;
    },
    isFirstArtworkMinQuantityInvalid() {
      const quantity = this.artworksState[0]?.quantity;
      return this.sticker && quantity && quantity < this.minStickerQuantity;
    },
    removeBgLoading() {
      return this.loading.removeBg;
    },
    isUVDTFSticker() {
      return this.catalogNumber === "UVDTF01";
    },
    letterNumberArtwork() {
      if (this.isJerseyNumbers || this.isLetters) {
        const [artwork] = this.artworks;
        const { summary } = this;
        const retailPrice = artwork.quantity
          ? Number(summary.orderTotal || 0) / artwork.quantity
          : 0;
        const unitPrice = artwork.quantity
          ? Number(summary.orderSubtotal || 0) / artwork.quantity
          : 0;
        const percentage = retailPrice ? 100 * (1 - unitPrice / retailPrice) : 0;
        return {
          ...artwork,
          retailPrice,
          unitPrice,
          percentage
        };
      }

      return null;
    },
    isValidJerseyArtwork() {
      const { isValid, height, sizes, quantities } = this.letterNumberArtwork;
      return isValid && height && Object.keys(sizes).length && Object.keys(quantities).length;
    },
    isLetterPreviewReady() {
      const { style, font, height, maxLength, color, texts, textIndex } = this.letterNumberArtwork;
      const quantity = (texts || []).filter(Boolean).length;
      return style && font && height && maxLength && color && quantity && textIndex !== null;
    },
    pickFromUploadsOrDesigns() {
      return !this.isSpangle && !this.printAsIs;
    },
    initialValues() {
      if (this.printAsIs) {
        return GANG_DEFAULT_VALUES;
      }
      if (this.sticker) {
        return DEFAULT_STICKER_VALUES;
      }
      if (this.isPuff) {
        return DEFAULT_PUFF_VALUES;
      }
      if (this.isJerseyNumbers) {
        return JERSEY_NUMBERS_DEFAULT_VALUES;
      }

      return DEFAULT_VALUES;
    },
    showMultiSize() {
      return this.showMultiSizeArtwork;
    },
    showStepsUI() {
      return (
        !this.isJerseyNumbers && !this.isLetters && !this.spangleConfig.isSpangle && !this.sticker
      );
    },
    isGangSheetProduct() {
      return this.catalogNumber && this.gangSheetCatalogNumbers.includes(this.catalogNumber);
    },
    showRedrawUI() {
      return (
        Boolean(this.enableRedraw) &&
        Boolean(this.forcePdpTransfersRedraw) &&
        !this.isGangSheetProduct &&
        !this.isLetters &&
        !this.isJerseyNumbers &&
        // Products with post-processing after having the final image
        !this.sticker &&
        !this.spangleConfig.isSpangle &&
        !this.isGoldFoil &&
        !this.isPuff
      );
    },
    showNewGangSheetErrorMessage() {
      return !this.showModal;
    },
    isRedrawDelayTextVisible() {
      return this.experiments.show_redraw_delay_text === 1;
    },
    isCustomerReviewOnRedrawsEnabled() {
      return this.customerReviewOnRedraws && this.customerReviewOnTransferRedraws;
    },
    isStandardArtwork() {
      return (
        !this.printAsIs &&
        !this.isProColor &&
        !this.isSticker &&
        !this.isStickerSample &&
        !this.spangleConfig.isSpangle &&
        !this.isLetters &&
        !this.isJerseyNumbers &&
        !this.isUVDTFSticker
      );
    },
    isBackendTriggeredEnhancementEligible() {
      return BACKEND_TRIGGERED_ENHANCEMENT_ELIGIBLE_CATALOG.includes(this.catalogNumber);
    },
    enableAbAiMonetization() {
      return this.catalogNumber === "DTF001" && this.experiments.ab_ai_monetization_v4 === 1;
    },
    isFaceliftExperimentActive() {
      return (
        this.catalogNumber === "DTF001" &&
        this.experiments.ab_transfers_pdp_facelift_october_2025 === 1
      );
    },
    showDesignServicesDeliveryBuffer() {
      return !this.isJiffyMade && this.experiments.transfers_design_services_delivery_buffer === 1;
    },
    // Helper getter to validate individual artwork
    isArtworkValid() {
      return artwork => {
        const validQuantity =
          this.sticker || this.isPuff
            ? artwork.quantity >= this.minStickerQuantity
            : artwork.quantity;

        const sizes = this.showMultiSize
          ? artwork.sizesAndQuantities.filter(({ quantity }) => Number(quantity) > 0)
          : artwork.sizesAndQuantities;

        const validMultiQuantity = this.showMultiSize
          ? sizes.length > 0 && sizes.every(({ quantity }) => Number(quantity) > 0)
          : validQuantity;

        const isValidColor = !this.isPuff || Boolean(artwork.color);
        const artworkEnhancementProcessing =
          this.isStandardArtwork && !artwork.isLoading && !artwork.enhancementCompleted;

        const customNoteValid = this.getCustomNoteValid(artwork);
        const customRedrawStateValid = this.getCustomRedrawStateValid(artwork);

        return (
          artwork.isValid &&
          !artwork.uploadError &&
          !artwork.isLoading &&
          validMultiQuantity &&
          isValidColor &&
          !artworkEnhancementProcessing &&
          customNoteValid &&
          customRedrawStateValid
        );
      };
    },
    // Helper to validate custom notes
    getCustomNoteValid() {
      return artwork => {
        const customNote = artwork.enhancementNote;
        if (artwork.enhancementTier === "custom") {
          return customNote && customNote.trim().length > 0;
        }
        if (artwork.enhancementTier === "full_enhancements") {
          return (
            !this.paidExpertEditAddonEnabled ||
            (artwork.hasExpertEditAddon ? customNote && customNote.trim().length > 0 : true)
          );
        }
        return true;
      };
    },
    // Helper to validate custom redraw state
    getCustomRedrawStateValid() {
      return artwork => {
        if (artwork.enhancementTier !== "custom") return true;
        const smartRedrawNotesEnabled = this.experiments.ab_smart_redraw_notes === 1;
        const customNote = artwork.enhancementNote;
        return smartRedrawNotesEnabled
          ? artwork.llmInterpretationState?.currentState === "final"
          : customNote && customNote.trim().length > 0;
      };
    }
  },
  actions: {
    // eslint-disable-next-line max-lines-per-function
    addFiles(files, startingIndex) {
      const { widthEdited, width, height, enhancementNote } = this.simulation;

      const {
        spangleConfig: { isSpangle }
      } = this;

      const enableCustomSize = widthEdited && !isSpangle;

      const initialValuesForSize = this.printAsIs ? GANG_DEFAULT_VALUES : DEFAULT_VALUES;

      const mappedFiles = [...files].map((file, index) => {
        const initialValues = {
          width: initialValuesForSize.width,
          height: initialValuesForSize.height,
          previewMetadata: initialValuesForSize.previewMetadata
        };
        const dimensions = enableCustomSize && index === 0 ? { width, height } : initialValues;

        const quantity = index === 0 ? this.simulation?.quantity : initialValuesForSize.quantity;

        return {
          ...emptyArtworkItem,
          file,
          enhancementNote,
          enhanceImage: true,
          isFile: true,
          name: file.name,
          quantity,
          ...dimensions,
          sizesAndQuantities: [
            {
              quantity,
              ...dimensions,
              preset: "custom",
              id: getRandomId()
            }
          ]
        };
      });

      this.simulation.enhancementNote = "";

      if (this.isJiffyMade) {
        const newArtworks = [...this.artworksState];
        newArtworks.splice(startingIndex, mappedFiles.length, ...mappedFiles);
        this.artworksState = newArtworks;
      } else {
        this.artworksState = [...this.artworksState, ...mappedFiles];
      }
    },
    setFileLoading(index) {
      this.artworksState = [...this.artworksState];
      this.artworksState[index] = {
        ...this.artworksState[index],
        originalIndex: index,
        isLoading: true
      };
    },
    setSimulation(simulation) {
      this.simulation = { ...this.simulation, ...simulation };
    },
    setBlankItemsCount(count) {
      this.blankItemsCount = count;
    },
    setTransfersInCartCount(count) {
      this.transfersInCartCount = count;
    },
    setIsNewToTransfers(value) {
      this.isNewToTransfers = value;
    },
    // eslint-disable-next-line max-lines-per-function
    setLoadedArtwork({ index, isBuyAgain, artwork }) {
      this.artworksState = [...this.artworksState];
      const preparedArtwork = objNamesToCamelCase(artwork);

      const { metadata } = preparedArtwork;
      let { sizesAndQuantities } = preparedArtwork;
      let artworkIndex = index;
      const defaultDimensions = {
        width: metadata.widthInch,
        height: metadata.heightInch
      };

      let dimensions = this.showMultiSizeArtwork
        ? calculateDefaultDimensions({ aspectRatio: metadata.aspectRatio })
        : defaultDimensions;

      if (!artworkIndex) {
        artworkIndex = findIndexById(this.artworksState, artwork.id);
      }

      const { isSizesEdited } = this.artworks[artworkIndex] || {};

      const { widthEdited, width } = this.simulation;

      const {
        spangleConfig: { isSpangle }
      } = this;

      if (widthEdited && !isSpangle && artworkIndex === 0) {
        dimensions = calculateDimensions(metadata.aspectRatio, width, this.sticker);
      }

      const initialValuesForSize = this.printAsIs ? GANG_DEFAULT_VALUES : DEFAULT_VALUES;

      const quantity =
        artworkIndex === 0 ? this.simulation?.quantity : initialValuesForSize.quantity;
      const defaultSizesAndQuantities = [
        {
          ...dimensions,
          quantity,
          preset: "custom",
          id: getRandomId()
        }
      ];

      if (this.showMultiSizeArtwork) {
        if (isSizesEdited) {
          sizesAndQuantities = recalculateCustomSizes(
            this.artworks[artworkIndex].sizesAndQuantities,
            metadata.aspectRatio,
            this.sticker
          );
        } else {
          sizesAndQuantities = isBuyAgain ? sizesAndQuantities : defaultSizesAndQuantities;
        }
      } else {
        sizesAndQuantities = [];
      }

      this.artworksState[artworkIndex] = {
        ...this.artworksState[artworkIndex],
        ...preparedArtwork,
        isLoading: false,
        isFile: false,
        isSimulated: false,
        removeBackground: metadata.removeBackground,
        originalWidth: metadata.widthInch,
        originalHeight: metadata.heightInch,
        lengthFoot: metadata.lengthFoot,
        lastWebSocketEvent: null,
        lastWebSocketEventData: {},
        ...dimensions,
        sizesAndQuantities
      };
    },
    setCachedArtworkPrices(prices) {
      this.cachedArtworkPrices = Object.keys(prices).reduce((acc, price) => {
        acc[price] = objNamesToCamelCase(prices[price]);
        return acc;
      }, {});
    },
    setFailedUploadArtwork({ index, artworkId, uploadError }) {
      let artworkIndex = index;

      if (!artworkIndex) {
        artworkIndex = findIndexById(this.artworksState, artworkId);
      }

      if (!this.artworksState[artworkIndex]) return;

      this.artworksState[artworkIndex].uploadError = uploadError;
    },
    /* eslint-disable max-lines-per-function */
    setArtworkPricing({ index, isMultiSize, ...responseData }) {
      const pricingData = responseData.response;
      const preparedData = objNamesToCamelCase(pricingData);
      const {
        bulkDiscount,
        orderSubtotal,
        orderTotal,
        productCartQuantity,
        artworks,
        enhancements,
        bogoPromotionData
      } = preparedData;

      // Store BOGO promotion data
      if (bogoPromotionData) {
        this.bogoPromotionData = bogoPromotionData;
      }

      if (this.isJerseyNumbers || this.isLetters) {
        this.simulation = {
          ...this.simulation,
          prices: artworks
        };

        this.summary = {
          bulkDiscount,
          orderSubtotal,
          orderTotal
        };
        return;
      }

      if (artworks.simulation && !this.isJiffyMade) {
        this.simulation = {
          ...this.simulation,
          ...(isMultiSize ? artworks.simulation[0] : artworks.simulation),
          id: "simulation",
          sizesAndQuantities: isMultiSize
            ? zipArray(this.simulation.sizesAndQuantities, artworks.simulation)
            : []
        };
        this.summary = initialState.summary;
        this.isPricingLoading = false;
        return;
      }
      const ids = Object.keys(artworks);

      const updatedArtworks = [...this.artworksState];
      ids.forEach(id => {
        let itemIndex = findIndexById(this.artworksState, id);

        if (itemIndex === -1) itemIndex = index;
        if (!this.artworksState[itemIndex]) return;

        updatedArtworks[itemIndex] = {
          ...this.artworksState[itemIndex],
          pricing: isMultiSize ? artworks[id][0] : artworks[id],
          sizesAndQuantities: isMultiSize
            ? zipArrayByKey(this.artworksState[itemIndex].sizesAndQuantities, artworks[id], "id")
            : []
        };
      });
      this.artworksState = updatedArtworks;
      this.summary = {
        bulkDiscount,
        orderSubtotal,
        orderTotal,
        productCartQuantity,
        enhancements
      };
      this.isPricingLoading = false;

      // Trigger a reload of delivery estimates if the delivery date response is available.
      // Timeout is used to ensure that the state is updated before the reload.
      setTimeout(() => deliveryDateResponse.reloadDeliveryEstimatesWithoutFetch(), 0);
    },
    /* eslint-enable max-lines-per-function */
    setSimulationPricing({ artworks, isMultiSize }) {
      const sizesAndQuantities = {
        ...this.simulation.sizesAndQuantities[0],
        ...artworks.simulation
      };
      this.simulation = {
        ...this.simulation,
        ...objNamesToCamelCase(artworks.simulation),
        sizesAndQuantities: isMultiSize ? [sizesAndQuantities] : []
      };
      this.isPricingLoading = false;
    },
    updateArtworkValues({ index, ...values }) {
      if (!this.artworksState.length) {
        this.simulation = {
          ...this.simulation,
          ...values
        };
        return;
      }
      const updatedArtworks = [...this.artworksState];
      let artworkIndex = updatedArtworks.findIndex(
        element => String(index) === String(element.originalIndex)
      );

      if (this.isJiffyMade && artworkIndex === -1) {
        artworkIndex = index;
      }

      updatedArtworks[artworkIndex] = { ...updatedArtworks[artworkIndex], ...values };
      this.artworksState = updatedArtworks;
    },
    updateArtworkValuesById({ id, ...values }) {
      if (!this.artworksState.length) {
        return;
      }
      const updatedArtworks = [...this.artworksState];
      const index = findIndexById(updatedArtworks, id);
      const preparedArtwork = objNamesToCamelCase(values, MIXPANEL_METADATA_KEYS);

      updatedArtworks[index] = { ...updatedArtworks[index], ...preparedArtwork };
      if (values.metadata && !preparedArtwork.width) {
        updatedArtworks[index].width = preparedArtwork.metadata.widthInch;
        updatedArtworks[index].height = preparedArtwork.metadata.heightInch;
      }
      if (Object.prototype.hasOwnProperty.call(values, "removeBackground")) {
        updatedArtworks[index].metadata.removeBackground = values.removeBackground;
      }
      this.artworksState = updatedArtworks;
    },
    setRemovalFile(id) {
      this.fileToRemove = id;
    },
    setPreCut(preCut) {
      this.preCut = preCut;
    },
    setPreCutPrice(price) {
      this.preCutPrice = price;
    },
    setPreCutEnabled(preCutEnabled) {
      this.preCutEnabled = preCutEnabled;
    },
    setPreCutDuration(preCutDuration) {
      this.preCutDuration = preCutDuration;
    },
    setLoadFromConfig(loadFromConfig) {
      this.loadFromConfig = loadFromConfig;
    },
    setEnablePreCut() {
      this.enablePreCut = true;
    },
    resetRemovalFile() {
      this.fileToRemove = null;
    },
    reindexArtworks() {
      this.artworksState = this.artworksState.map((artwork, newIndex) => ({
        ...artwork,
        originalIndex: newIndex
      }));
    },
    removeArtwork(id) {
      if (id) {
        transfersPollingService.stopPolling(id);
      }

      this.artworksState = this.artworksState.filter(artwork => artwork.id !== id);

      // Reindex remaining artworks to maintain consistent originalIndex values
      this.reindexArtworks();

      this.fileToRemove = null;
      if (this.sticker) {
        this.simulation = { ...this.simulation, width: null, height: null };
      }
      this.spangles.completedSpangleData = [];
      if (!this.artworksState.length) {
        this.showModal = false;
      }
    },
    clearWorkspace() {
      this.cleanupPolling();

      this.artworksState = [];
      this.summary = initialState.summary;
      this.simulation = { ...DEFAULT_SIMULATION_ARTWORK };
    },
    openModal() {
      this.showModal = true;
    },
    hideModal() {
      this.showModal = false;
    },
    setShowCTA(showCTA) {
      this.showCTA = showCTA;
    },
    setAllowForceBgRemove(allowForceBgRemove) {
      this.allowForceBgRemove = allowForceBgRemove;
    },
    setCanvaDtfEnabled(canvaDtfEnabled) {
      this.canvaDtfEnabledState = canvaDtfEnabled;
    },
    setEnableMobileFooterCTA(enableMobileFooterCTA) {
      this.enableMobileFooterCTA = enableMobileFooterCTA;
    },
    setCanvaReviewer(canvaReviewer) {
      this.canvaReviewer = canvaReviewer;
    },
    setVariantId(variantId) {
      this.variantId = variantId;
    },
    setProductId(productId) {
      this.productId = productId;
    },
    setProductSlug(productSlug) {
      this.productSlug = productSlug;
    },
    setFavorites(favorites) {
      this.favorites = favorites;
    },
    setPdpType(pdpType) {
      this.pdpType = pdpType;
    },
    setCatalogNumber(catalogNumber) {
      this.catalogNumber = catalogNumber;
    },
    setMessagingConsents(messagingConsents) {
      this.messagingConsents = messagingConsents;
    },
    setPrintAsIs(printAsIs) {
      this.printAsIs = printAsIs;
    },
    setNeckLabel(neckLabel) {
      this.neckLabel = neckLabel;
    },
    setSticker(sticker) {
      this.sticker = sticker;
    },
    setStickerQrScanned(stickerQrScanned) {
      this.stickerQrScanned = stickerQrScanned;
    },
    setDtfMaxWidth(dtfMaxWidth) {
      this.dtfMaxWidth = dtfMaxWidth;
    },
    setDtfMaxHeight(dtfMaxHeight) {
      this.dtfMaxHeight = dtfMaxHeight;
    },
    setDtfMinWidth(dtfMinWidth) {
      this.dtfMinWidth = dtfMinWidth;
    },
    setDtfMinHeight(dtfMinHeight) {
      this.dtfMinHeight = dtfMinHeight;
    },
    setIsRetrying(isRetrying) {
      this.isRetrying = isRetrying;
    },
    setRetryFailed(value) {
      this.artworksState.forEach(artwork => {
        if (!artwork.id) {
          artwork.retryFailed = value;
        }
      });
    },
    setIsLoading(value) {
      this.artworksState.forEach(artwork => {
        if (!artwork.id) {
          artwork.isLoading = value;
        }
      });
    },
    setShowDiscardModal() {
      this.showDiscardModal = true;
    },
    hideDiscardModal() {
      this.showDiscardModal = false;
    },
    dropMultiArtwork() {
      this.showModal = false;
      this.summary = initialState.summary;
      const notUploadedArtworks = this.artworksState.filter(
        artwork => artwork.isFile || artwork.isLoading
      );
      this.artworksState = [];
      const initialValuesForSize = this.printAsIs ? GANG_DEFAULT_VALUES : DEFAULT_VALUES;
      this.simulation = { ...DEFAULT_SIMULATION_ARTWORK, ...initialValuesForSize };

      this.cleanupPolling();

      if (!notUploadedArtworks.length) return;
      this.discarded = new Date();
      this.fetchController?.abort();
      this.fetchController = null;
    },
    removeArtworkByIndex(index) {
      const artworkToRemove = this.artworksState.find(artwork => artwork.originalIndex === index);
      if (artworkToRemove?.id) {
        transfersPollingService.stopPolling(artworkToRemove.id);
      }

      this.artworksState = this.artworksState.filter(artwork => artwork.originalIndex !== index);

      // Reindex remaining artworks to maintain consistent originalIndex values
      this.reindexArtworks();

      if (!this.artworksState.length) {
        this.showModal = false;
      }
    },
    setEmptyArtwork(id) {
      this.artworksState = [
        ...this.artworksState,
        {
          ...emptyArtworkItem,
          id,
          isLoading: true,
          isFile: false,
          originalIndex: this.artworksState.length
        }
      ];
    },
    setDiscarded(value) {
      this.discarded = value;
    },
    setFetchController(signal) {
      this.fetchController = signal;
    },
    setUseDefaultValues(useDefaultValues) {
      this.useDefaultValues = useDefaultValues;
    },
    setValidationErrorMessage({ index, message, isMultiSizeError, type }) {
      const error = {
        errorMessage: message,
        isMultiSizeError,
        isValid:
          !type ||
          !Object.values(ErrorTypes).some(symbol => symbol.description === type.description)
      };

      if (type !== undefined) {
        error.errorType = type;
      }

      if (!this.artworksState.length) {
        this.simulation = {
          ...this.simulation,
          ...error
        };
        return;
      }

      const updatedArtworks = [...this.artworksState];
      if (!updatedArtworks[index]) return;

      updatedArtworks[index] = { ...updatedArtworks[index], ...error };
      this.artworksState = updatedArtworks;
      this.artworksState = [...this.artworksState];
    },
    setLocale(locale) {
      this.locale = locale;
    },
    setReplaceArtwork(replace) {
      this.replaceArtwork = replace;
    },
    resetFilesError() {
      this.showFilesError = false;
    },
    setShowArtworkHistoryModal(show) {
      this.showArtworkHistoryModal = show;
    },
    setArtworkHistory(history) {
      this.artworkHistory = history;
    },
    setArtworkHistoryPagination(pageInfo) {
      this.artworkHistoryPagination = { ...this.artworkHistoryPagination, ...pageInfo };
    },
    setUserType(type) {
      this.userType = type;
    },
    setIsMobile(isMobile) {
      this.isMobile = isMobile;
    },
    setPrintingServiceName(printingServiceName) {
      this.printingServiceName = printingServiceName;
    },
    setDtfChannelUuid(uuid) {
      this.dtfChannelUuid = uuid;
    },
    setActionCableOptions(options) {
      this.actionCableOptions = options;
    },
    setActionCableConnected(value) {
      this.actionCableConnected = value;
    },

    handlePollingEnhancementComplete(artwork) {
      const artworkIndex = this.artworks.findIndex(a => Number(a.id) === Number(artwork.id));
      if (artworkIndex === -1) {
        return;
      }

      this.clearValidateArtworkStartEventInterval(artwork.id);
      this.updateEnhancedArtwork(artwork);
      this.updatePrintReadyInfoState(artwork.id);
      this.updatePreviewDpi({ artworkId: artwork.id });
      this.downscaleOriginalImage({ artworkId: artwork.id });
      if (this.isGoldFoil) {
        this.generateGoldFoilImages({ artworkId: artwork.id });
        this.convertImageToHexColor({ artwork });
      }

      if (!this.artworks.some(a => a.isLoading)) {
        this.getArtworksPricing();
      }

      this.dispatchTrackingEvents(artwork);
    },
    dispatchTrackingEvents(artwork) {
      submitDtfEvent("image_processing_completed", {
        payload: {
          process_data: artwork,
          stream_name: `dtf_channel_${this.dtfChannelUuid}`,
          action_type: "enhancement_complete",
          worker_enhancement: true
        },
        business_line: "transfers",
        artwork_id: artwork.id,
        completion_source: "polling_fallback"
      });

      trackRudderstackImageProcessingCompleted({
        artwork_id: artwork.id,
        dpi: artwork.metadata?.dpi,
        location: artwork.metadata?.uploaded_on,
        file_name: artwork.filename,
        file_type: artwork.filetype,
        file_size: artwork.filesize,
        business_line: this.businessLine,
        artworks_count: this.artworks.length,
        pre_cut_transfers: this.preCut
      });

      this.updateArtworkValuesById({
        id: artwork.id,
        imageProcessingCompletedEventSent: true
      });

      api.dtf.acknowledgeArtwork({ id: artwork.id }).catch(() => {
        // Silently ignore errors
      });
    },
    setMockupColorForArtwork(artwork) {
      const whitePixelsRatio = Number(artwork.metadata?.white_pixels_ratio || 0);
      const artworkIndex = this.artworks.findIndex(item => item.id === artwork.id);
      if (artworkIndex !== -1) {
        this.setPresetMockupColor({
          index: artworkIndex,
          color: whitePixelsRatio > 50 ? "#000000" : "#E1E1E1"
        });
      }
    },
    getEnhancedArtworkImageUrls(artwork) {
      const useNoBg = artwork.removeBackground || artwork.metadata?.remove_background;
      const imageUrl = useNoBg
        ? artwork.noBgUrl || artwork.imageUrl
        : artwork.withBgUrl || artwork.imageUrl;
      const enhancedImageUrl = artwork.noBgUrl || artwork.imageUrl;

      return {
        imageUrl,
        enhancedImageUrl,
        url: imageUrl,
        sliderPreviewUrl: artwork.noBgNoTrimUrl || artwork.noBgUrl,
        previewUrl: imageUrl,
        displayUrl: imageUrl,
        trimmedWithBgUrl: artwork.withBgUrl,
        trimmedNoBgUrl: artwork.noBgUrl,
        trimmedImageUrl: imageUrl
      };
    },
    getEnhancedArtworkDimensions(artwork) {
      const useNoBg = artwork.removeBackground || artwork.metadata?.remove_background;
      const correctMetadata = useNoBg
        ? artwork.enhancedMetadata || artwork.metadata
        : artwork.withBgMetadata || artwork.metadata;
      const widthInch = correctMetadata?.width_inch || correctMetadata?.widthInch;
      const heightInch = correctMetadata?.height_inch || correctMetadata?.heightInch;

      return {
        width: widthInch,
        height: heightInch,
        originalWidth: artwork.originalMetadata?.width_inch || artwork.originalMetadata?.widthInch,
        originalHeight:
          artwork.originalMetadata?.height_inch || artwork.originalMetadata?.heightInch
      };
    },
    getEnhancedArtworkSizes(artwork, dimensions) {
      const existingArtwork = this.artworks.find(a => a.id === artwork.id);
      let sizesAndQuantities = existingArtwork?.sizesAndQuantities || [];

      if (this.showMultiSize && (!sizesAndQuantities.length || !existingArtwork?.isSizesEdited)) {
        sizesAndQuantities = [
          {
            width: dimensions.width,
            height: dimensions.height,
            quantity: this.initialValues.quantity,
            preset: "custom",
            id: getRandomId()
          }
        ];
      }

      return sizesAndQuantities;
    },
    updateEnhancedArtwork(artwork) {
      this.setLoadedArtwork({ artwork });
      this.setMockupColorForArtwork(artwork);

      const dimensions = this.getEnhancedArtworkDimensions(artwork);
      const imageUrls = this.getEnhancedArtworkImageUrls(artwork);
      const sizesAndQuantities = this.getEnhancedArtworkSizes(artwork, dimensions);

      this.updateArtworkValuesById({
        id: artwork.id,
        enhancementCompleted: true,
        enhancedNoBgUrl: artwork.noBgUrl,
        enhancedWithBgUrl: artwork.withBgUrl,
        ...imageUrls,
        ...dimensions,
        metadata: artwork.metadata,
        enhancedMetadata: artwork.enhancedMetadata,
        originalMetadata: artwork.originalMetadata,
        withBgMetadata: artwork.withBgMetadata,
        canRemoveBg: artwork.canRemoveBg,
        removeBackground: artwork.removeBackground || artwork.metadata?.remove_background,
        originalImageUrl: artwork.originalImageUrl,
        originalMime: artwork.metadata?.mime_type,
        enhancedDpi: artwork.metadata?.dpi,
        originalDpi: artwork.originalMetadata?.dpi || 300,
        enhanceImage: true,
        lastWebSocketEvent: "enhancement_complete_via_polling",
        lastWebSocketEventData: {
          source: "polling_fallback",
          data: artwork
        },
        isLoading: false,
        progress: 100,
        progressMessage: "Complete",
        sizesAndQuantities
      });
    },
    handleWebSocketEnhancementComplete(artworkId) {
      transfersPollingService.stopPolling(artworkId);
    },
    cleanupPolling() {
      transfersPollingService.stopAllPolling();
    },
    schedulePollingFallback(artworkId) {
      if (!artworkId || !transfersPollingService.isPollingEnabled(this.catalogNumber)) return;

      const artworkExists = this.artworks.some(
        artwork => artwork.id && Number(artwork.id) === Number(artworkId)
      );

      if (!artworkExists) {
        return;
      }

      // Check if polling is already active for this artwork
      if (transfersPollingService.isPolling(artworkId)) {
        return;
      }

      transfersPollingService.startPolling(
        artworkId,
        enhancedArtwork => {
          this.handlePollingEnhancementComplete(enhancedArtwork);
        },
        {
          catalogNumber: this.catalogNumber,
          dtfChannelUuid: this.dtfChannelUuid,
          checkArtworkExists: id =>
            this.artworks.some(artwork => artwork.id && Number(artwork.id) === Number(id))
        }
      );
    },
    setSpangleAlgorithms(algorithms) {
      this.spangleConfig.spangleAlgorithms = algorithms;
    },
    setIsSpangle(isSpangle) {
      this.spangleConfig.isSpangle = isSpangle;
    },
    setSpangleColors(spangleColors) {
      this.spangleConfig.spangleColors = spangleColors;
    },
    setCable(cable) {
      this.cable = cable;
    },
    setBusinessLine(businessLine) {
      this.businessLine = businessLine;
    },
    setIsPuff(isPuff) {
      this.isPuff = isPuff;
    },
    setIsProColor(isProColor) {
      this.isProColor = isProColor;
    },
    setIsSticker(isSticker) {
      this.isSticker = isSticker;
    },
    setIsJiffyMade(isJiffyMade) {
      this.isJiffyMade = isJiffyMade;
    },
    setIsStickerSample(isStickerSample) {
      this.isStickerSample = isStickerSample;
    },
    setIsLetters(isLetters) {
      this.isLetters = isLetters;
    },
    toggleLoading([key, value]) {
      this.loading[key] = value;
    },
    setExperiments(experiments) {
      this.experiments = experiments;
    },
    setDtfCategory(dtfCategory) {
      this.dtfCategory = dtfCategory;
    },
    setPreCutUpsellThreshold(preCutUpsellThreshold) {
      this.preCutUpsellThreshold = Number(preCutUpsellThreshold);
    },
    setPuff(puffData) {
      this.puff = { ...this.puff, ...puffData };
    },
    resetPuff() {
      this.puff = { ...DEFAULT_PUFF_VALUES };
    },
    setIsJerseyNumbers(isJerseyNumbers) {
      this.isJerseyNumbers = isJerseyNumbers;
    },
    setPrintReadyInfoShow(show) {
      this.printReadyInfoConfig.show = show;
    },
    setPrintReadyInfoExpandable(expanded) {
      this.printReadyInfoConfig.expanded = expanded;
    },
    setArtworkBackground(color) {
      this.artworkBackground = color;
    },
    setBackgroundType(backgroundType) {
      this.backgroundType = backgroundType;
    },
    setPreviewCanvasSize({ width, height }) {
      this.previewCanvasSize = { width, height };
    },
    setLetterPreviewMessage({ textIndex, message }) {
      this.letterPreviewMessages[textIndex] = message;
    },
    clearLetterPreviewMessages() {
      this.letterPreviewMessages = {};
    },
    removeLetterPreviewMessage(index) {
      const letterPreviewMessagesCopy = { ...this.letterPreviewMessages };

      delete letterPreviewMessagesCopy[index];

      this.letterPreviewMessages = Object.entries(letterPreviewMessagesCopy).reduce(
        (newMessages, [, value], newIndex) => {
          newMessages[newIndex] = value;
          return newMessages;
        },
        {}
      );
    },
    setLetterPreviewLoading(value) {
      this.letterPreviewLoading = value;
    },
    setIsGoldFoil(value) {
      this.isGoldFoil = value;
    },
    setForcePdpTransfersRedraw(value) {
      this.forcePdpTransfersRedraw = value;
    },
    setEnableRedraw(value) {
      this.enableRedraw = value;
    },
    setIsPricingLoading(value) {
      this.isPricingLoading = value;
    },
    setShowMultiSize(value) {
      this.showMultiSizeArtwork = value;
    },
    setGangSheetBuilder(value) {
      this.gangSheetBuilder = value;
    },
    setShowGangSheetBuilder(value) {
      this.showGangSheetBuilder = value;
    },
    setGeneratedGangSheets(value) {
      this.generatedGangSheets = value;
    },
    setGangSheetBuilderShopId(value) {
      this.gangSheetBuilderShopId = value;
    },
    setShowGangSheetBuilderFeature(value) {
      this.showGangSheetBuilderFeature = value;
    },
    setIsGangSheetBuilder(value) {
      this.isGangSheetBuilder = value;
    },
    setGangSheetBuilderVariants(value) {
      this.gangSheetBuilderVariants = value;
    },
    updateGangSheetBuilderSettings(value) {
      this.gangSheetBuilderSettings = {
        ...this.gangSheetBuilderSettings,
        ...value
      };
    },
    setReorderEnabled(value) {
      this.reorderEnabled = value;
    },
    setIsDtfSheet(value) {
      this.isDtfSheet = value;
    },
    setGangSheetBulkPrice(value) {
      this.gangSheetBulkPrice = {
        ...this.gangSheetBulkPrice,
        ...value
      };
    },
    setGangSheetBuilderSizes(value) {
      this.gangSheetBuilderSizes = value;
    },
    setStoreCreditInfo(info) {
      this.storeCreditInfo = info;
    },
    setPresetMockupColor({ index, color }) {
      this.presetMockupColor[index] = color;
    },
    removePresetMockupColor(index) {
      this.presetMockupColor.splice(index, 1);
    },
    setGangSheetBuilderCarousels(value) {
      this.gangSheetBuilderCarousels = value;
    },
    setPreloadedDesignId(value) {
      this.preloadedDesignId = value;
    },
    setValidateArtworkStartTime(artworkId, time) {
      this.validateArtworkStartTime[artworkId] = time;
    },
    setValidateArtworkStartEventIntervalID(artworkId, id) {
      this.validateArtworkStartEventIntervalID[artworkId] = id;
    },
    setPaidExpertEditAddonEnabled(value) {
      this.paidExpertEditAddonEnabled = value;
    },
    setKlaviyoSmsTransactionalSubscribed(value) {
      this.klaviyoSmsTransactionalSubscribed = value;
    },
    setSmsTransactionalSubscriptionPdpEnabled(value) {
      this.smsTransactionalSubscriptionPdpEnabled = value;
    },
    setTransferPdpInactivityDetection(value) {
      this.transferPdpInactivityDetection = value;
    },
    setSmsTransactionalPhone(value) {
      this.smsTransactionalPhone = value;
    },
    setSmsTransactionalSubscribeButtonText(value) {
      this.smsTransactionalSubscribeButtonText = value;
    },
    setGangSheetCatalogNumbers(catalogNumbers) {
      this.gangSheetCatalogNumbers = catalogNumbers;
    },
    setCustomerReviewOnRedraws(value) {
      this.customerReviewOnRedraws = value;
    },
    setCustomerReviewOnTransferRedraws(value) {
      this.customerReviewOnTransferRedraws = value;
    },
    setDtfWorkerEnhancement(value) {
      this.dtfWorkerEnhancement = value;
    },
    setDtfBackendTriggeredEnhancement(value) {
      this.dtfBackendTriggeredEnhancement = value;
    },
    setTransferPdpBackButtonDetection(value) {
      this.transferPdpBackButtonDetection = value;
    },
    clearValidateArtworkStartEventInterval(artworkId) {
      this.validateArtworkStartTime[artworkId] = null;
      clearInterval(this.validateArtworkStartEventIntervalID[artworkId]);
      this.validateArtworkStartEventIntervalID[artworkId] = null;
    },
    async addSampleToCart() {
      const payload = {
        ...getFormPayload(),
        sample_pack: true,
        artworks: [
          {
            artwork_id: 0,
            width: 1,
            height: 1,
            quantity: 1,
            variant_id: this.variantId,
            sample_pack: true
          }
        ]
      };

      api.dtf.addToCart(payload).then(_res => {
        window.location = "/cart";
      });
    },
    /* eslint-disable max-lines-per-function */
    async addFilesToQueue({ files, artworkIndex, existingArtworkId, uploadSource = null }) {
      // Add files if we are not updating an artwork's file or
      // the current artwork is simulation

      // Set upload_source to catalog_number_PDP if not already set
      if (!uploadSource) {
        uploadSource = `${this.catalogNumber}_PDP`;
      }

      submitDtfEvent("image_upload_count", {
        business_line: this.businessLine,
        image_count: files.length
      });

      if (artworkIndex === null || this.artworks[0].isSimulated) {
        this.addFiles(files, artworkIndex ?? this.artworks.length);
      }

      // Reset specified artwork by index
      if (artworkIndex !== null) {
        const { widthEdited, width, height } = this.simulation;

        const enableCustomSize = widthEdited && !this.isSpangle && artworkIndex === 0;

        let dimensions = {
          width: this.initialValues.width,
          height: this.initialValues.height
        };

        if (enableCustomSize) {
          dimensions = {
            width: this.printAsIs ? this.initialValues.width : width,
            height
          };
        }

        const quantity =
          artworkIndex === 0 && !this.isSticker
            ? this.simulation.quantity
            : this.initialValues.quantity;

        this.setIsRetrying(false);
        this.updateArtworkValues({
          index: artworkIndex,
          ...emptyArtworkItem,
          ...this.initialValues,
          ...dimensions,
          quantity,
          file: files[0],
          isFile: true,
          name: files[0].name,
          errorMessage: null,
          sizesAndQuantities: [
            {
              ...dimensions,
              quantity,
              preset: "custom",
              id: getRandomId()
            }
          ]
        });
      }

      if (this.modalArtworks.length > 1) {
        this.openModal();
      }

      const uploadedTimeStamp = new Date();
      const stateFiles = this.artworks.reduce((res, item, index) => {
        if (item.isFile) {
          return [
            ...res,
            {
              ...item,
              originalIndex: index,
              uploadedTimeStamp
            }
          ];
        }
        return res;
      }, []);

      // load files one by one
      // eslint-disable-next-line no-restricted-syntax
      for (const file of stateFiles) {
        // If artwork index is specified only update that artwork
        if (
          (typeof artworkIndex === "number" && artworkIndex !== file.originalIndex) ||
          this.artworks[file.originalIndex]?.retryFailed
        )
          // eslint-disable-next-line no-continue
          continue;

        if (!this.discarded || file.uploadedTimeStamp > this.discarded) {
          this.setFileLoading(file.originalIndex);
          // eslint-disable-next-line no-await-in-loop
          this.uploadArtwork({
            file,
            artworkIndex,
            existingArtworkId,
            uploadSource: uploadSource || `${this.catalogNumber}_PDP`
          });
        }
      }
    },
    async uploadArtwork({ file, isDropEvent, artworkIndex, existingArtworkId, uploadSource }) {
      const { printAsIs, pdpType, preCut, businessLine, catalogNumber } = this;
      const { valid, message } = isFileTypeSupported(file.file, printAsIs);
      if (!valid) {
        this.setFailedUploadArtwork({
          index: file.originalIndex,
          uploadError: message
        });
        logUnsupportedFileTypeEvent(file.file, {
          page: "pdp",
          pdp_type: pdpType,
          business_line: businessLine
        });

        return;
      }
      const uploadUuid = this.trackImageUploadStart(file, isDropEvent);
      const maxRetries = 15;
      const { dtfChannelUuid } = this;
      let retryCount = 0;
      const currentArtworkIndex = artworkIndex || file.originalIndex;
      const handleOnlineStatusChange = async () => {
        if (this.artworks[currentArtworkIndex]?.retryFailed) return;

        if (navigator.onLine && this.isRetrying) this.setIsRetrying(false);
        await uploadWithRetry(currentArtworkIndex);
      };

      window.addEventListener("online", handleOnlineStatusChange);
      window.addEventListener("offline", handleOnlineStatusChange);

      const uploadWithRetry = async () => {
        const controller = new AbortController();
        const { signal } = controller;
        const { productId } = this;
        const formData = new FormData();
        const processTimeStamp = Date.now();

        this.setFetchController(controller);
        formData.append("upload_source", uploadSource);
        formData.append("file", file.file);
        formData.append("product_id", productId);
        formData.append("file_original_index", file.originalIndex);
        formData.append("dtf_channel_uuid", dtfChannelUuid);
        formData.append("pre_cut", preCut);
        formData.append("process_time_stamp", processTimeStamp);
        formData.append("experiments", JSON.stringify(this.experiments || {}));
        if (existingArtworkId) {
          formData.append("existing_artwork_id", existingArtworkId);
        }

        const generatedGangSheetData =
          this.generatedGangSheets.length > 0 &&
          this.generatedGangSheets.find(gangSheet => gangSheet.id === file.file.name.split(".")[0]);

        if (generatedGangSheetData) {
          formData.append("generated_gang_sheet_data", JSON.stringify(generatedGangSheetData));
        }
        this.updateArtworkValues({
          index: currentArtworkIndex,
          progress: 0,
          processTimeStamp
        });

        const uploadStartTime = performance.now();
        let artwork = null;

        try {
          artwork = await api.dtf.uploadArtwork(formData, {
            raw: true,
            useAxiosForRequest: true,
            headers: { "Content-Type": undefined },
            signal,
            onUploadProgress: ({ progress }) => {
              if (progress === 1) {
                this.updateArtworkValues({
                  index: currentArtworkIndex,
                  progress: DTF_PROGRESS_MAPPING.upload_artwork.percentage[0],
                  progressMessage: "Processing file"
                });
              } else {
                this.updateArtworkValues({
                  index: currentArtworkIndex,
                  progress: DTF_PROGRESS_MAPPING.upload_artwork.percentage[0]
                });
              }
              this.setIsRetrying(false);
            }
          });

          const artworkObj = this.artworks.find(
            art => art.originalIndex?.toString() === file.originalIndex.toString()
          );

          if (
            (this.discarded && file.uploadedTimeStamp < this.discarded) ||
            file.file !== artworkObj?.file ||
            artworkObj?.retryFailed
          )
            return;
          if (this.isRetrying) this.setIsRetrying(false);

          const { widthEdited, width, height } = this.simulation;

          const dimensions = {
            width: this.initialValues.width,
            height: this.initialValues.height
          };

          const enableCustomSize = widthEdited && !this.isSpangle && currentArtworkIndex === 0;

          if (enableCustomSize) {
            dimensions.width = this.printAsIs ? this.initialValues.width : width;
            dimensions.height = height;
          }

          let defaultQuantity =
            currentArtworkIndex === 0 && !this.isSticker
              ? this.simulation.quantity
              : this.initialValues.quantity;

          const previewMetadata = this.printAsIs ? { ...GANG_DEFAULT_VALUES.previewMetadata } : {};

          if (generatedGangSheetData) {
            defaultQuantity = generatedGangSheetData.quantity;
            previewMetadata.heightInch = generatedGangSheetData.size.height;
          }

          this.updateArtworkValues({
            ...artwork,
            ...this.initialValues,
            ...dimensions,
            index: currentArtworkIndex,
            lastWebSocketEvent: "upload_artwork",
            lastWebSocketEventData: {
              data: artwork.data
            },
            pricing: this.cachedArtworkPrices[emptyArtworkItem.enhancementTier] || {},
            quantity: defaultQuantity,
            sizesAndQuantities: [
              {
                ...dimensions,
                quantity: defaultQuantity,
                preset: "custom",
                id: getRandomId()
              }
            ],
            previewMetadata,
            fromGangSheetBuilder: this.generatedGangSheets.length > 0,
            catalogNumber
          });

          if (retryCount > 0) {
            submitDtfEvent("image_upload_retries_success", {});
            trackRudderstackImageUploadRetry({
              status: "success",
              file_name: file.file.file_name,
              file_type: file.file.type,
              file_size: file.file.size,
              location: "pdp",
              dpi: 300,
              pre_cut_transfers: this.preCut
            });
          }

          this.trackImageUploadCompleted({
            file: file.file,
            uploadStartTime,
            artwork,
            uploadUuid
          });

          if (this.isStandardArtwork) {
            const attrToUpdate = {
              ...objNamesToCamelCase(artwork),
              index: currentArtworkIndex,
              quantity: this.simulation.quantity
            };

            this.updateArtworkValues({
              ...attrToUpdate,
              originalDpi: artwork.metadata?.dpi,
              sizeBasedDpi: artwork.metadata?.dpi,
              enhancedDpi: 300
            });

            this.schedulePollingFallback(artwork.id);
            this.setLoadedArtwork({ artwork });
            this.updatePreviewDpi({ artworkId: artwork.id });
            await this.downscaleOriginalImage({ artworkId: artwork.id });
          }

          if (artwork.metadata?.errors?.length > 0) {
            this.setGangsheetValidationError({
              index: currentArtworkIndex,
              artwork,
              type: ErrorTypes.INVALID_GANG_SHEET
            });
          }
        } catch (e) {
          if (e.name === "AbortError" || e.name === "CanceledError") return;

          const errorResponse = e?.response?.data || e;
          const handledError = ["backend_file_validation", "backend_processing_error"].includes(
            errorResponse?.error_type
          );

          if (
            (errorResponse.error && !errorResponse.success) ||
            (navigator.onLine && e.message !== "Network Error")
          ) {
            let uploadError;
            if (handledError) {
              uploadError = errorResponse.error;
              // Use the artwork response if available, otherwise pass the error response
              // TODO: properly distinguish artwork object, response object and error object
              this.trackImageUploadCompleted({
                file: file.file,
                response: errorResponse,
                uploadStartTime,
                artwork: artwork || errorResponse,
                uploadUuid
              });
            } else {
              uploadError = "Upload error";
              logUploadError({
                error_message: e.message,
                error: errorResponse.error,
                handledError,
                uploadError,
                errorResponse
              });
            }
            const pageInfo = {
              pageValue: "pdp",
              businessLine: this.businessLine
            };
            logUploadErrorEvent(pageInfo, file.file, e);
            this.setFailedUploadArtwork({
              index: file.originalIndex,
              uploadError
            });

            return;
          }

          if (retryCount < maxRetries) {
            this.setIsRetrying(true);
            const retryInterval = RETRY_INTERVALS[retryCount];
            retryCount += 1;
            submitDtfEvent("image_upload_retry", {});
            setTimeout(() => uploadWithRetry(artworkIndex), retryInterval);
          } else {
            submitDtfEvent("image_upload_retries_failed", {});
            const errorContext = getErrorContext("pdp", file.file, e);
            submitDtfEvent("image_upload_network_error", errorContext);
            trackRudderstackImageUploadRetry({
              status: "failed",
              file_name: errorContext.file_name,
              file_type: errorContext.file_type,
              file_size: errorContext.file_size,
              dpi: errorContext.dpi,
              location: errorContext.page,
              business_line: errorContext.business_line,
              pre_cut_transfers: this.preCut
            });
            this.setRetryFailed(true);
            this.setIsLoading(true);
            this.fetchController?.abort();
            this.setFetchController(null);
          }
        } finally {
          window.removeEventListener("online", handleOnlineStatusChange);
          window.removeEventListener("offline", handleOnlineStatusChange);
        }
      };

      // Start the initial upload attempt
      await uploadWithRetry(artworkIndex);
    },
    /* eslint-enable max-lines-per-function */
    downloadAndTriggerArtworkUploadFlow(artworks) {
      artworks.forEach((artwork, index) => {
        fetch(artwork.originalImageUrl)
          .then(response => response.blob())
          .then(blob => {
            const file = new File([blob], artwork.name, { type: artwork.originalMimeType });
            this.addFilesToQueue({
              files: [file],
              artworkIndex: index,
              existingArtworkId: artwork.id
            });
          });
      });
    },
    handleProcessError({ file, errorResponse }) {
      if (errorResponse.error && !errorResponse.success) {
        let uploadError;
        if (
          ["backend_file_validation", "backend_processing_error"].includes(errorResponse.error_type)
        ) {
          uploadError = errorResponse.error;
        } else {
          uploadError = "Processing error";

          window.Honeybadger?.notify("Upload artwork error", {
            params: {
              error_message: errorResponse.message,
              error: errorResponse.error,
              error_response: errorResponse,
              uploadError
            }
          });
        }

        const pageInfo = {
          pageValue: "pdp",
          businessLine: this.businessLine
        };
        logUploadErrorEvent(pageInfo, file, {
          response: {
            data: errorResponse
          }
        });
        this.setFailedUploadArtwork({
          index: file.originalIndex,
          uploadError
        });
      }
    },
    async updateArtworkContent({ data }) {
      this.getArtworksPricing();
      this.updatePdpDates();
      this.updateArtworkValuesById(data);
    },
    /* eslint-disable max-lines-per-function */
    async getSimulationPricing(dimensions = {}) {
      const { productId, preCut, showMultiSize } = this;
      const data = {
        artwork_id: "simulation",
        width: Number(dimensions.width) || 1,
        height: Number(dimensions.height) || 1,
        enhancement_tier: this.simulation.enhancementTier || "full_enhancements"
      };
      const pricingData = await api.dtf.dtfPricing(
        { product: productId },
        {
          artworks: [data],
          pre_cut: preCut,
          ...(this.isJiffyMade ? { request_source: "jiffy_made" } : {})
        }
      );

      const storeCredits = objNamesToCamelCase({
        order_subtotal: pricingData.order_subtotal,
        user_store_credits: pricingData.user_specific_store_credits,
        expected_credits: pricingData.expected_transfers_store_credit,
        transfers_cart_subtotal: pricingData.transfers_cart_subtotal,
        tiers: pricingData.transfers_store_credit_tiers
      });

      this.setStoreCreditInfo(storeCredits);

      EventBus.publish(Events.PRICING_UPDATED, storeCredits);

      this.setSimulationPricing({
        artworks: pricingData.artworks,
        isMultiSize: showMultiSize
      });
    },
    async getArtworksPricing() {
      const { artworks, productId, preCut, isJerseyNumbers, isLetters, showMultiSize } = this;
      let data = [];
      let response;

      if (showMultiSize) {
        artworks.forEach(artwork =>
          artwork.sizesAndQuantities
            .filter(sizeQty => Number(sizeQty.quantity) > 0)
            .forEach(sizeQty => {
              const payload = {
                artwork_id: `${artwork.id}--${sizeQty.id}`,
                width: Number(sizeQty.width) || 1,
                height: Number(sizeQty.height) || 1,
                quantity: Number(sizeQty.quantity),
                enhancement_tier: artwork.enhancementTier || "full_enhancements",
                has_expert_edit_addon: artwork.hasExpertEditAddon || false
              };

              if (this.isFaceliftExperimentActive && !this.isJiffyMade) {
                payload.enhancement_request_note = artwork.enhancementNote || "";
              }

              data.push(payload);
            })
        );
      } else if (this.isJiffyMade) {
        data = artworks
          .filter(artwork => !artwork.isSimulated)
          .map(artwork => ({
            artwork_id: artwork.id,
            width: Number(artwork.width) || 1,
            height: Number(artwork.height) || 1,
            quantity: Number(artwork.quantity),
            enhancement_tier: artwork.enhancementTier || "full_enhancements",
            has_expert_edit_addon: artwork.hasExpertEditAddon || false
          }));
      } else {
        data = (isJerseyNumbers || isLetters ? this.letterNumberArtworks : artworks).map(
          artwork => {
            const payload = {
              artwork_id: artwork.id || "simulation",
              width: Number(artwork.width) || 1,
              height: Number(artwork.height) || 1,
              quantity: Number(artwork.quantity),
              enhancement_tier: artwork.enhancementTier || "full_enhancements",
              has_expert_edit_addon: artwork.hasExpertEditAddon || false
            };

            if (this.isFaceliftExperimentActive && !this.isJiffyMade) {
              payload.enhancement_request_note = artwork.enhancementNote || "";
            }

            return payload;
          }
        );
      }

      if (!data.length) return;

      this.setIsPricingLoading(true);

      this.updatePricingAbortSignal?.abort();
      this.updatePricingAbortSignal = new AbortController();
      try {
        response = await api.dtf.dtfPricing(
          { product: productId },
          {
            artworks: data,
            pre_cut: preCut,
            ...(this.isJiffyMade ? { request_source: "jiffy_made" } : {})
          },
          {
            useAxiosForRequest: true,
            signal: this.updatePricingAbortSignal.signal
          }
        );
      } catch (error) {
        if (error.message === "canceled" || error.code === "ERR_CANCELED") {
          return;
        }

        window.Honeybadger?.notify("Error fetching artworks pricing", {
          params: {
            error_message: error.message,
            product_id: productId,
            pre_cut: preCut,
            is_jiffy_made: this.isJiffyMade,
            artworks: data,
            show_multi_size: showMultiSize
          }
        });
        return;
      }

      if (showMultiSize) {
        response = {
          ...response,
          artworks: groupByKeyPrefix(response.artworks, "--")
        };
      }

      const storeCredits = objNamesToCamelCase({
        order_subtotal: response.order_subtotal,
        user_store_credits: response.user_specific_store_credits,
        expected_credits: response.expected_transfers_store_credit,
        transfers_cart_subtotal: response.transfers_cart_subtotal,
        tiers: response.transfers_store_credit_tiers
      });

      this.setStoreCreditInfo(storeCredits);

      this.setArtworkPricing({ response, isMultiSize: showMultiSize });
      EventBus.publish(Events.PRICING_UPDATED, storeCredits);
    },
    async initDefaultValues({ skipDeliveryDateLoading = false } = {}) {
      const {
        initialValues,
        isJerseyNumbers,
        isLetters,
        variantId,
        catalogNumber,
        loadFromConfig
      } = this;

      if (!loadFromConfig && !isJerseyNumbers && !isLetters) return;

      try {
        const response = await api.dtf.getInitConfig({ catalog_number: catalogNumber });
        const { success, error, applyDefaultValues, preCut, dtfChannelUuid, messagingConsents } =
          objNamesToCamelCase(response);

        if (!success) {
          // attempt to retrieve error from server if present
          throw new Error(error);
        }

        const pdpDateRequest = {
          ...initialValues,
          variant_id: variantId,
          artwork_id: "simulation"
        };

        if (!loadFromConfig && (isJerseyNumbers || isLetters)) {
          if (isLetters) {
            this.generateLettersPreview(true);
          }

          reloadDeliveryEstimates([pdpDateRequest], preCut);
          return;
        }

        // Only set up cable connection if we're not in enhancement loading flow
        if (!this.isEnhancementLoading) {
          this.setDtfChannelUuid(dtfChannelUuid);

          const cable = initCable(this);
          this.setCable(cable);
        }

        if (messagingConsents) {
          this.setMessagingConsents(messagingConsents);
        }

        if (applyDefaultValues) {
          this.setPreCut(preCut);
          this.setEnablePreCut();

          if (!skipDeliveryDateLoading) {
            reloadDeliveryEstimates([pdpDateRequest], preCut);
          }

          const defaultValues = structuredClone(initialValues);

          // Ensure the default values use the correct enhancement tier based on experiments
          // The simulation object already has the correct enhancement tier set from experiments
          defaultValues.enhancementTier = this.simulation.enhancementTier;

          await this.getSimulationPricing(defaultValues);
          if (isJerseyNumbers || isLetters) {
            defaultValues.height = null;
          }

          if (this.showMultiSize) {
            defaultValues.sizesAndQuantities = [
              {
                ...defaultValues,
                preset: "custom",
                id: getRandomId()
              }
            ];
          }

          this.updateArtworkValues(defaultValues);
        } else {
          this.updatePdpDates();
        }

        this.setUseDefaultValues(applyDefaultValues);
      } catch (error) {
        if (error.message === "Unable to connect to server") {
          // eslint-disable-next-line no-alert
          alert(
            "Unable to connect to server. Please check your internet connection and try again."
          );
        } else {
          const errorParams = {};
          Object.getOwnPropertyNames(error).forEach(key => {
            errorParams[key] = error[key];
          });

          window.Honeybadger?.notify("Could not get dtf init meta", {
            params: {
              ...errorParams,
              catalog_number: this.catalogNumber,
              load_from_config: this.loadFromConfig,
              url: window?.location?.href
            }
          });
        }
      }
    },
    /* eslint-enable max-lines-per-function */
    async updatePreCut(preCut) {
      this.setPreCut(preCut);
      this.getArtworksPricing();
      this.updatePdpDates();
    },
    // eslint-disable-next-line max-lines-per-function
    async trackArtworkAddToCart(artwork) {
      const { formArtworks, variantId, catalogNumber, businessLine, isStandardArtwork } = this;

      const variant = await getVariantInfo(variantId);
      const artworkMetadata = artwork.metadata;
      const artworkBorneoMetadata = artwork.borneo_metadata;

      const trackProperties = {
        brand: variant.product.brand,
        product_category: variant.product.adwords_category,
        product: variant.product.name,
        spree_composition_id: variant.compositionId,
        composition: variant.color,
        fulfillment_group: variant.fulfillmentGroup || "",
        sku: variant.sku,
        quantity: artwork.quantity,
        buy_it_again: getIsBuyItAgainStatus(),
        favorite: variant.favorite,
        softness_score: variant.softnessScore,
        dtf_width: artwork.width,
        dtf_height: artwork.height,
        dtf_area: artwork.width * artwork.height,
        from_dam: artwork.from_dam,
        multi_artwork: formArtworks.length > 1,
        dtf_image_souce: artworkMetadata.imageSource,
        business_line: businessLine,
        pre_cut: this.preCut,
        background_removed: artworkMetadata.removeBackground,
        background_color: artwork.bg_color,
        border_size: artwork.padding_size,
        shape: artwork.shape,
        redraw_requested: artwork.enhancement_request_note,
        multi_size: artwork.sizes_and_quantities?.length > 1,
        enhancement_tier: artwork.enhancement_tier,
        catalog_number: variant.product.catalog_number
      };

      if (isStandardArtwork) {
        trackProperties.enhance_image = artwork.enhanceImage;
      }

      if (["SPNG-HGR001", "SPNG-GLS001", "SPNG-RNB001"].includes(catalogNumber)) {
        const spanglesStore = useSpanglesStore();
        trackProperties.dtf_spangles_algorithm = spanglesStore.selectedAlgorithm;
        trackProperties.artwork_url = spanglesStore.originalArtworkUrl;
      }

      if (this.isPuff) {
        trackProperties.color = artwork.color;
      }

      // additional params for borneo images
      if (artworkMetadata.imageSource === "borneo") {
        trackProperties.dtf_borneo_fonts_count = artworkBorneoMetadata.fonts.fonts?.length || 0;
        trackProperties.dtf_borneo_font_details = artworkBorneoMetadata.fonts.font_details;

        const summary = summarizeFonts(artworkBorneoMetadata.fonts?.font_details);
        trackProperties.dtf_borneo_non_unique_fonts = summary.fontFamilies;
        trackProperties.dtf_borneo_fonts = [...new Set(summary.fontFamilies)];
        trackProperties.dtf_borneo_non_unique_effects = summary.textEffects;
        trackProperties.dtf_borneo_effects = [...new Set(summary.textEffects)];
        trackProperties.dtf_borneo_images_count = artworkBorneoMetadata.images.total_images;
        trackProperties.dtf_borneo_texts_count = artworkBorneoMetadata.fonts.total_texts;
        trackProperties.dtf_borneo_shapes_count = artworkBorneoMetadata.elements.total_shapes;
        trackProperties.dtf_borneo_enhancements = artworkBorneoMetadata.enhancements;
        trackProperties.dtf_borneo_project_id = artwork.project_id;
      }

      if (this.isJerseyNumbers || this.isLetters) {
        const itemDetails = {
          style: artwork.style,
          height: artwork.height,
          color: artwork.color
        };

        if (this.isLetters) {
          itemDetails.font = artwork.font;
          itemDetails.texts = artwork.texts.filter(Boolean);
          itemDetails.max_length = artwork.maxLength;
        }
        // quantities":{"1":5,"2":2,"3":1}
        if (this.isJerseyNumbers && Object.keys(artwork.quantities).length) {
          Object.keys(artwork.quantities).forEach(key => {
            itemDetails[`number_${key}`] = artwork.quantities[key];
          });
        }

        trackProperties.item_details = itemDetails;
      }

      if (artwork.from_gang_sheet_builder) {
        trackProperties.gang_sheet_builder_variant = artwork.catalog_number;
      }

      if (this.showMultiSize && artwork.sizes_and_quantities?.length > 1) {
        submitDtfEvent("multiple_sizes_added", {
          number_of_sizes: artwork.sizes_and_quantities.length,
          brand: variant.product.brand,
          product_category: variant.product.adwords_category,
          product: variant.product.name,
          spree_composition_id: variant.compositionId,
          composition: variant.color
        });

        artwork.sizes_and_quantities.forEach(sq => {
          submitDtfEvent("product_added", {
            ...trackProperties,
            dtf_width: sq.width,
            dtf_height: sq.height,
            dtf_area: sq.width * sq.height,
            quantity: sq.quantity,
            predefined_placement: sq.preset,
            placement_size: sq.size
          });
        });
      } else {
        trackProperties.predefined_placement = "custom";
        submitDtfEvent("product_added", trackProperties);
      }
    },
    async submitPrintReorder(selectedArtworks) {
      try {
        const response = await api.dtf.addToCartPrint({
          variant_id: this.variantId,
          quantity: 1,
          dtf_print_ids: selectedArtworks.map(artwork => artwork.dtfPrintId),
          source_page: "design_library"
        });
        const prices = response.artwork_prices;
        const currency = this.locale === "en-CA" ? "C$" : "$";
        const dtfData = selectedArtworks.map(({ id, dtfPrintId, url, metadata }) => ({
          id,
          catalogNumber: "Jiffy Transfers",
          productId: this.productId,
          variantId: this.variantId,
          color: "",
          image: url,
          quantity: prices[dtfPrintId].quantity,
          size: `${metadata.widthInch}" x ${metadata.heightInch}${this.printAsIs ? "'" : '"'}`,
          price: `${currency}${Number(prices[dtfPrintId].price).toFixed(2)}`,
          preCut: false,
          preCutPrice: 0
        }));
        incrementHeaderCounter(selectedArtworks.length);
        EventBus.publish(Events.MOBILE_GRID_SUBMIT, { dtfData });
        EventBus.publish(Events.ITEMS_ADDED_TO_CART, response);
      } catch (error) {
        window.Honeybadger?.notify("Error adding to cart", {
          params: { error: JSON.stringify(error) }
        });
      }
    },
    // eslint-disable-next-line max-lines-per-function
    submitForm() {
      const payload = getFormPayload();
      const { formArtworks, preCut, isReadyForOrder } = this;
      const artworkValues = Object.values(formArtworks);
      let hasEmptyValues = false;
      artworkValues.find(artwork => !artwork.quantity);

      if (this.showMultiSize) {
        hasEmptyValues = artworkValues.find(artwork =>
          artwork.sizes_and_quantities?.find(sq => !sq.quantity)
        );
      } else {
        hasEmptyValues = artworkValues.find(artwork => !artwork.quantity);
      }

      if (!isReadyForOrder) {
        return;
      }

      if (!artworkValues.length || hasEmptyValues) {
        validation.show({ hasFormErrors: true });
        return;
      }

      const requestData = {
        ...payload,
        artworks: formArtworks,
        pre_cut: preCut
      };

      mobileApp.mq.publish("feedback", { type: "vibration" });

      const $$modal = $("#multi-artwork-modal");
      if (this.showModal) {
        this.hideModal();
        $$modal.on("hidden.bs.modal", () => {
          $$modal.off("hidden.bs.modal");
          idleCallback(() =>
            EventBus.publish(Events.MOBILE_GRID_SUBMIT, {
              dtfData: this.showMultiSize ? this.csmDataMultiSize : this.csmData
            })
          );
        });
      } else {
        idleCallback(() =>
          EventBus.publish(Events.MOBILE_GRID_SUBMIT, {
            dtfData: this.showMultiSize ? this.csmDataMultiSize : this.csmData
          })
        );
      }

      api.dtf
        .addToCart(requestData)
        .then(response => {
          if (response.success) {
            incrementHeaderCounter(this.totalQuantity);
            EventBus.publish(Events.ITEMS_ADDED_TO_CART, response);
            formArtworks.forEach(async artwork => await this.trackArtworkAddToCart(artwork));
            trackAddToCart(this.totalQuantity, response.cart_total);
            this.clearWorkspace();
            this.resetPuff();
            return;
          }

          throw new Error({ message: "Add To Cart response is not successful" });
        })
        .catch(e => {
          $("#continue-shopping-modal").modal("hide");
          const errorResponse = tryParse(e.message);

          if (errorResponse?.error === "Session expired") {
            redirectTo("/login");
          } else {
            $("#adding-failure").modal("show");
            $("#adding-failure").find("#adding-failure-error").html(errorResponse?.error_message);

            if (typeof errorResponse !== "object") {
              // only on non-JSON or malformed server response
              window.Honeybadger?.notify("Add to cart error", {
                params: { error_message: e.message }
              });
            }

            submitDtfEvent("add_to_cart_error", { error: e.message });
            throw new Error(e);
          }
        });
    },
    updatePdpDates() {
      const { formArtworks, preCut, isJerseyNumbers, isLetters, showMultiSize, artworks } = this;
      let filledArtworks = [];

      if (showMultiSize) {
        formArtworks.forEach(artwork => {
          artwork.sizes_and_quantities
            .filter(({ quantity }) => !!quantity)
            .forEach((sizeQty, index) => {
              filledArtworks.push({
                artwork_id: `${artwork.artwork_id}-${index}`,
                ...sizeQty
              });
            });
        });
      } else {
        filledArtworks =
          isJerseyNumbers || isLetters
            ? this.letterNumberArtworks
            : formArtworks.filter(({ quantity }) => !!quantity);
      }

      const designServicesEnabled = artworks.some(
        artwork => artwork.hasExpertEditAddon && artwork.enhancementNote?.trim()
      );

      reloadDeliveryEstimates(filledArtworks, preCut, designServicesEnabled);
    },
    updateArtworkStyle({ id, isBgRemoved }) {
      this.toggleLoading(["removeBg", true]);

      const { artworks, isSpangle } = this;
      const artwork = artworks.find(artworkItem => String(id) === String(artworkItem.id));
      const metadata = isBgRemoved ? artwork.metadata : artwork.withBgMetadata;
      const url = isBgRemoved ? artwork.noBgUrl : artwork.withBgUrl;

      let originalWidth = metadata.widthInch;
      let originalHeight = metadata.heightInch;

      if (artwork.enhanceImage) {
        originalWidth = artwork.withBgMetadata?.originalWidthInch || originalWidth;
        originalHeight = artwork.withBgMetadata?.originalHeightInch || originalHeight;
      }

      this.updateArtworkValuesById({
        id,
        isLoading: false,
        removeBackground: isBgRemoved,
        width: metadata.widthInch,
        height: metadata.heightInch,
        originalWidth,
        originalHeight,
        errorMessage: null,
        toggleBackgroundError: null,
        originalDpi: 300,
        url
      });

      if (isSpangle) {
        const spanglesStore = useSpanglesStore();
        spanglesStore.setOriginalArtworkUrl(url);
      }
    },
    // eslint-disable-next-line max-lines-per-function
    async sendUpdateArtworkStyle({
      id,
      isBgRemoved,
      updateMetadataCache = false,
      shape = null,
      forceBgRemoval = false
    }) {
      try {
        const artwork = this.artworks.find(item => String(item.id) === String(id)) || {};
        this.toggleLoading(["removeBg", true]);
        this.updateArtworkValuesById({ id, processingImage: true });
        const sticker = this.isSticker || this.isStickerSample;

        // let paddingSize = "no_padding";
        // if (sticker) {
        //   paddingSize =
        //     artwork.paddingSize === "no_padding"
        //       ? DEFAULT_STICKER_VALUES.paddingSize
        //       : artwork.paddingSize;
        // }
        const response = await api.dtf.updateArtworkStyle(
          { id },
          {
            remove: isBgRemoved,
            catalog_number: this.catalogNumber,
            enhancement_tier: artwork.enhancementTier,
            options: {
              padding_size: artwork.paddingSize,
              shape: artwork.shape,
              force_bg_removal: forceBgRemoval
            }
          },
          {
            raw: true,
            useAxiosForRequest: true,
            headers: { "Content-Type": undefined }
          }
        );

        let data = {};
        if (sticker) {
          const responseData = objNamesToCamelCase(response);
          const { metadata, stickerCutlineWithBgUrl, stickerCutlineNoBgUrl, noBgUrl, withBgUrl } =
            responseData;
          data = { metadata, stickerCutlineWithBgUrl, stickerCutlineNoBgUrl, noBgUrl, withBgUrl };

          if (updateMetadataCache && shape) {
            const dimensions = this.isStickerSample
              ? { width: metadata.widthInch, height: metadata.heightInch }
              : getStickerDimensions(metadata.widthInch, metadata.heightInch);
            data = { ...data, ...dimensions };

            // dispatch("stickers/updateArtworkDataCache", {
            //   id,
            //   shape,
            //   paddingSize,
            //   data
            // });
          }
        } else {
          data.url = response.remove_background ? response.url : response.with_bg_url;
          data.withBgMetadata = response.with_bg_metadata;
        }

        this.updateArtworkValuesById({ id, processingImage: false, ...data });
        this.getArtworksPricing();
        this.updatePdpDates();
      } catch (e) {
        const errorResponse = e?.response?.data || {
          error: "Something went wrong. Please, try again!"
        };
        // revert it
        this.updateArtworkStyle({ id, isBgRemoved: !isBgRemoved });
        this.updateArtworkValuesById({
          id,
          toggleBackgroundError: errorResponse.error,
          processingImage: false
        });
      } finally {
        this.toggleLoading(["removeBg", false]);
        this.updateArtworkValuesById({ id, processingImage: false });
      }
    },
    async buyArtworksAgainFromOrder(orderNumber) {
      const artworks = await api.dtf.getArtworksFromOrder({ order_number: orderNumber });

      this.downloadAndTriggerArtworkUploadFlow(
        artworks.map(artwork => ({
          id: artwork.id,
          name: artwork.name,
          originalImageUrl: artwork.url,
          originalMimeType: artwork.filetype
        }))
      );
    },
    /* eslint-disable max-lines-per-function */
    async buySpangleAgain({ spangledArtwork }) {
      const { catalogNumber, dtfChannelUuid } = this;
      const spanglesStore = useSpanglesStore();

      // spangled true - artwork selected from 'Previous Purchases' under 'Spangle' tab
      const spangleDuplicateAction = spangledArtwork.spangled ? "min" : "all";

      const artwork = await api.dtf.getArtwork(
        { id: spangledArtwork.id },
        {
          catalog_number: catalogNumber,
          dtf_channel_uuid: dtfChannelUuid,
          spangle_duplicate_action: spangleDuplicateAction,
          upload_source: "transfers"
        }
      );

      const preparedArtwork = objNamesToCamelCase({
        ...spangledArtwork,
        ...artwork,
        success: true
      });

      if (spangledArtwork.spangled) {
        this.setLoadedArtwork({
          artwork: preparedArtwork,
          index: 0
        });
      } else {
        this.setEmptyArtwork(null);
        this.updateArtworkValues({
          index: 0,
          ...preparedArtwork,
          isLoading: true,
          isFile: false,
          removeBackground: preparedArtwork.metadata.removeBackground,
          width: preparedArtwork.metadata.widthInch,
          height: preparedArtwork.metadata.heightInch,
          originalWidth: preparedArtwork.metadata.widthInch,
          originalHeight: preparedArtwork.metadata.heightInch,
          lengthFoot: preparedArtwork.metadata.lengthFoot
        });
      }

      if (spangledArtwork.spangled) {
        spanglesStore.setIsSpangleReordering(true);

        const spangleVariation = {
          algorithm: "reorder",
          imageUrl: spangledArtwork.spangledImageUrl || artwork.url,
          spangleCsv: artwork.spangle_csv,
          spanglePagesInfo: artwork.metadata.spangle_pages_info,
          positionsJson: artwork.spangle_positions_json,
          isLoading: false,
          error: false
        };

        spanglesStore.setSpangleVariations([{ ...spangleVariation }]);
        spanglesStore.selectVersion({ version: { ...spangleVariation } });
      }

      spanglesStore.setOriginalArtworkUrl(artwork.url);

      let { width, height } = spangledArtwork;

      if (spangleDuplicateAction === "all") {
        width = artwork.metadata.width_inch;
        height = artwork.metadata.height_inch;
      }

      spanglesStore.setLastSpangledDimensions({ width, height });

      const mode = spangledArtwork.spangled ? SPANGLE_MODES.sizeEdited : SPANGLE_MODES.spangleIt;
      spanglesStore.setSpangleMode(mode);
    },
    /* eslint-enable max-lines-per-function */
    async generateCmykImages({ artwork }) {
      if (
        !artwork.annotateEnabled && // always generate CYMK when annotation is enabled
        (artwork.cmykPreviewImageNoBackgroundUrl || artwork.cmykPreviewImageWithBackgroundUrl)
      ) {
        return;
      }

      const cmykData = await api.dtf.generateCmykImages(
        { id: artwork.id },
        { catalog_number: this.catalogNumber }
      );

      this.updateArtworkValuesById({
        id: artwork.id,
        cmykPreviewImageNoBackgroundUrl: cmykData.cmyk_preview_image_no_background_url,
        cmykPreviewImageWithBackgroundUrl: cmykData.cmyk_preview_image_with_background_url
      });
    },
    generateSpangleImage({ artwork }) {
      const spanglesStore = useSpanglesStore();
      spanglesStore.prepareVariations({ isRetry: false });
      this.updateArtworkValuesById({ id: artwork.id, progress: 100 });

      api.dtf.generateSpangleImage(
        { id: artwork.id },
        {
          product_id: this.productId,
          dtf_channel_uuid: this.dtfChannelUuid,
          width_inch: artwork.width,
          height_inch: artwork.height,
          process_time_stamp: artwork.processTimeStamp
        }
      );
    },
    /* eslint-disable max-lines-per-function */
    async buyArtworksAgain(artworks, index) {
      const { artworks: currentArtworks } = this;
      let currentIndex = currentArtworks[0].id === "simulation" ? 0 : currentArtworks.length;

      if (this.isJiffyMade) {
        currentIndex = index;
      }

      const artworksToBuy = artworks.map(artwork => ({
        ...emptyArtworkItem,
        // Allow selecting the same image multiple times on Jiffy Made
        id: this.isJiffyMade ? `${artwork.id}-${currentIndex}` : artwork.id,
        name: artwork.name,
        url: artwork.url,
        metadata: artwork.metadata,
        width: artwork.metadata.widthInch,
        height: artwork.metadata.heightInch,
        lengthFoot: artwork.metadata.lengthFoot,
        quantity: this.initialValues.quantity,
        originalMime: artwork.originalMime,
        progress: 100,
        curProgress: 100,
        isFile: false,
        success: true,
        isLoading: true,
        // Preselect default prices for artwork
        pricing: this.cachedArtworkPrices[emptyArtworkItem.enhancementTier] || {},
        enhancementCompleted: artwork.enhancementCompleted,
        enhanceImage: artwork.enhanceImage,
        enhancedNoBgUrl: artwork.enhancedNoBgUrl,
        enhancedWithBgUrl: artwork.enhancedWithBgUrl,
        originalImageUrl: artwork.originalImageUrl,
        sliderPreviewUrl: artwork.sliderPreviewUrl,
        spangled: artwork.spangled,
        spangledImageUrl: artwork.spangledImageUrl,
        cutlineUrl: artwork.cutlineUrl,
        cmykPreviewImageWithBackgroundUrl: artwork.cmykPreviewImageWithBackgroundUrl,
        cmykPreviewImageNoBackgroundUrl: artwork.cmykPreviewImageNoBackgroundUrl,
        goildFoilPreviewAttached: artwork.goildFoilPreviewAttached,
        originalMetadata: artwork.originalMetadata,
        enhancedMetadata: artwork.enhancedMetadata,
        withBgMetadata: artwork.withBgMetadata,
        previewMetadata: artwork.previewMetadata,
        originalDpi: artwork.originalDpi,
        enhancedDpi: artwork.enhancedDpi,
        enhancedOriginalDpi: artwork.enhancedOriginalDpi,
        originalWidth: artwork.originalWidth,
        originalHeight: artwork.originalHeight,
        sizeBasedDpi: artwork.sizeBasedDpi,
        canRemoveBg: artwork.canRemoveBg,
        processTimeStamp: artwork.processTimeStamp
      }));

      if (this.isSpangle) {
        await this.buySpangleAgain({ spangledArtwork: artworksToBuy[0] });
      } else {
        let lastIndex = currentIndex;
        await Promise.all(
          artworksToBuy.map(async artwork => {
            let pdpArtworkIndex = this.artworks.findIndex(
              element => String(artwork.id) === String(element.id)
            );

            if (this.isJiffyMade) {
              pdpArtworkIndex = index;
            }

            const pdpArtwork = currentArtworks[pdpArtworkIndex];

            const sizes = pdpArtwork?.sizesAndQuantities;

            const sameSizeIndex = sizes?.findIndex(
              size =>
                `${size.width}${size.height}` === `${artwork.width}${artwork.height}` &&
                size.preset === "custom"
            );

            if (this.isGoldFoil && !artwork.goildFoilPreviewAttached && artwork.url) {
              const coloredImage = await convertImageToHexColor(artwork.url, GOLD_FOIL_HEX);
              artwork.url = coloredImage;
            }

            let artworkWidth = artwork.width;
            let artworkHeight = artwork.height;

            if (!artwork.enhanceImage) {
              artworkWidth = artwork.originalMetadata?.widthInch;
              artworkHeight = artwork.originalMetadata?.heightInch;
            }

            const updatedArtwork = {
              ...artwork,
              metadata: {
                ...artwork.metadata,
                widthInch: artworkWidth,
                heightInch: artworkHeight
              }
            };

            if (this.showMultiSize) {
              let updatedSizes = [];
              const size = {
                width: artwork.width,
                height: artwork.height,
                quantity: artwork.quantity,
                preset: "custom",
                id: getRandomId()
              };

              if (pdpArtwork) {
                updatedSizes = pdpArtwork?.sizesAndQuantities.map((sizeItem, sizeItemIndex) => ({
                  ...sizeItem,
                  quantity:
                    sameSizeIndex === sizeItemIndex ? sizeItem.quantity + 1 : sizeItem.quantity
                }));

                if (pdpArtwork && sameSizeIndex === -1) {
                  updatedSizes.push(size);
                }
              } else {
                updatedSizes = [size];
              }

              updatedArtwork.sizesAndQuantities = updatedSizes;
            } else if (pdpArtwork) {
              updatedArtwork.quantity = pdpArtwork.quantity + 1;
            }

            const newIndex = pdpArtwork ? pdpArtworkIndex : lastIndex;
            lastIndex = pdpArtwork ? lastIndex : lastIndex + 1;

            updatedArtwork.originalIndex = newIndex;

            this.setLoadedArtwork({
              artwork: updatedArtwork,
              isBuyAgain: true,
              index: this.isJiffyMade ? index : newIndex
            });
          })
        );

        // Check for gang sheet validation errors
        // Only validate for gang sheet products (check if catalog number is in gang sheet catalog numbers)
        const isGangSheetProduct =
          this.gangSheetCatalogNumbers.length > 0 &&
          this.gangSheetCatalogNumbers.includes(this.catalogNumber);

        if (isGangSheetProduct) {
          artworksToBuy.forEach(artwork => {
            const metadata = artwork.metadata || {};
            const errors = validateGangSheetDimensions(metadata);

            // Set validation error if any errors found
            if (errors.length > 0) {
              const artworkIndex = this.artworks.findIndex(
                element => String(artwork.id) === String(element.id)
              );
              if (artworkIndex !== -1) {
                this.setGangsheetValidationError({
                  index: artworkIndex,
                  artwork: {
                    id: artwork.id,
                    metadata: {
                      ...metadata,
                      errors
                    }
                  },
                  type: ErrorTypes.INVALID_GANG_SHEET
                });
              }
            }
          });
        }
      }

      if (this.artworks.length > 1) {
        this.openModal();
      }

      if (this.isProColor) {
        artworksToBuy.forEach(artwork => {
          this.generateCmykImages({ artwork });
        });
      }

      if (this.isSpangle) {
        // DO NOT call generateSpangleImage here for artworks from history
        // because buySpangleAgain creates a NEW artwork with a different ID
        // and we would be calling generateSpangleImage with the OLD ID
        // which causes ActionCable events to be ignored (ID mismatch)

        // If artwork needs spangle generation, we should call it with the NEW ID from store
        const storeArtwork = this.artworks[0];
        if (storeArtwork && storeArtwork.isLoading) {
          this.generateSpangleImage({ artwork: storeArtwork });
        }
      }

      this.getArtworksPricing();
      this.updatePdpDates();
    },
    async loadArtworkAndSetPaddingImage({ id, paddingSize }) {
      const artwork = await api.dtf.getArtwork(
        { id },
        {
          catalog_number: this.catalogNumber,
          dtf_channel_uuid: this.dtfChannelUuid,
          process_artwork: "false",
          file_content_type: null,
          padding_size: paddingSize
        }
      );

      this.updateArtworkValuesById({ ...artwork });
    },
    async preloadArtworks({
      artworkIds,
      artworkWidth,
      artworkHeight,
      processArtwork,
      urlPreCut,
      fileContentType,
      artworkPaddingSize,
      artworkBgColor,
      artworkColor,
      artworkShape
    }) {
      deleteCookie("uploaded_artwork_ids");
      this.cleanupUrlParameters();
      if (artworkIds.length > 1) {
        this.openModal();
      }

      // set preCut only if pre_cut URL param is set
      const preCut = tryParse(urlPreCut);
      // Only buy it again provides artwork width and height
      if (typeof preCut === "boolean") {
        this.setLoadFromConfig(false);
        this.setPreCut(preCut);
        this.setEnablePreCut();
      }

      const fetchArtworkPromises = artworkIds.map(async id => {
        if (!processArtwork) {
          this.updateArtworkValuesById({ id, progress: this.isProColor ? 90 : 100 });
        }

        this.updateArtworkValuesById({
          id,
          quantity: this.isSticker ? this.initialValues.quantity : this.simulation.quantity
        });

        const artworkIndex = findIndexById(this.artworks, id);

        const fetchArtworkWithRetry = async () => {
          let artwork;
          const delays = [300000, 700000, 900000, 1100000, 1300000]; // delays: 300s to 1300s
          const sticker = this.isSticker || this.isStickerSample;
          const retries = 5;
          const params = {
            catalog_number: this.catalogNumber,
            dtf_channel_uuid: this.dtfChannelUuid,
            process_artwork: processArtwork,
            file_content_type: fileContentType,
            process_time_stamp: this.artworks[artworkIndex]?.processTimeStamp || Date.now(),

            padding_size: artworkPaddingSize
          };

          for (let i = 0; i < retries; i += 1) {
            // eslint-disable-next-line no-await-in-loop
            artwork = await api.dtf.getArtwork({ id }, this.sanitizeObject(params));

            if (artwork && (!sticker || artwork.cutline_url)) {
              return artwork;
            }
            if (i === retries - 1) {
              this.setFailedUploadArtwork({
                index: 0,
                uploadError: "Processing error, try reloading page"
              });
            }

            // eslint-disable-next-line no-await-in-loop, no-promise-executor-return
            await new Promise(resolve => setTimeout(resolve, delays[i]));
          }

          return artwork;
        };

        let artwork;

        if (id) {
          artwork = await fetchArtworkWithRetry();
        }

        if (!artwork || artwork.metadata.process_cancelled) {
          this.removeArtwork(id);
          this.setSimulation({
            ...DEFAULT_SIMULATION_ARTWORK,
            ...this.initialValues,
            sizesAndQuantities: [
              {
                ...this.initialValues,
                preset: "custom",
                id: getRandomId()
              }
            ]
          });
          return;
        }

        if (this.isGoldFoil && artwork.url) {
          const coloredImage = await convertImageToHexColor(artwork.url, GOLD_FOIL_HEX);
          artwork.url = coloredImage;
        }

        if (this.isSpangle && !processArtwork) {
          await this.buyArtworksAgain([{ ...artwork }]);
        } else {
          this.updateArtworkValuesById({ ...artwork });
        }

        if (this.isProColor && !processArtwork) {
          await this.generateCmykImages({ artwork });
          this.updateArtworkValuesById({ id, progress: 100 });
        }

        if (artwork.metadata) {
          if (artwork.metadata.errors) {
            const index = this.artworks.length - 1;
            this.setGangsheetValidationError({
              index,
              artwork,
              type: ErrorTypes.INVALID_GANG_SHEET
            });
          }
          submitDtfEvent("external_image_loaded", {
            source: artwork.metadata.uploaded_on,
            business_line: this.businessLine
          });

          if (artwork.process_artwork !== "true" && !this.isStandardArtwork) {
            let artworkValues = {
              ...artwork,
              width: artworkWidth,
              height: artworkHeight,
              lengthFoot: artwork.metadata.length_foot,
              isLoading: false,
              isFile: false,
              removeBackground: artwork.metadata.remove_background,
              ...(this.showMultiSize && {
                sizesAndQuantities: [
                  {
                    width: artworkWidth,
                    height: artworkHeight,
                    quantity: this.initialValues.quantity,
                    preset: "custom",
                    id: getRandomId()
                  }
                ]
              })
            };

            if (this.printAsIs) {
              const previewMetadata = { heightInch: artwork.metadata.height_inch };
              artworkValues = {
                ...artworkValues,
                previewMetadata,
                isValid: true
              };
            }

            if (this.isSticker || this.isStickerSample) {
              artworkValues = {
                ...artworkValues,
                paddingSize: artworkPaddingSize,
                bgColor: artworkBgColor,
                shape: artworkShape
              };
            } else if (this.isPuff) {
              artworkValues = { ...artworkValues, color: artworkColor };
            }

            this.updateArtworkValuesById(artworkValues);
          } else if (this.isStandardArtwork) {
            const needProcess = artwork.process_artwork === "true";
            const artworkCamelCased = objNamesToCamelCase(artwork);
            const { originalMetadata, metadata, enhanceImage, enhancedMetadata, withBgMetadata } =
              artworkCamelCased;

            let selectedMetadata = originalMetadata;

            if (!needProcess && enhanceImage) {
              selectedMetadata = artworkCamelCased.removeBackground
                ? enhancedMetadata || metadata
                : withBgMetadata;
            }

            if (needProcess || !enhanceImage) {
              artworkWidth = originalMetadata?.widthInch;
              artworkHeight = originalMetadata?.heightInch;
            }

            const artworkValues = {
              ...artworkCamelCased,
              id: artwork.id,
              enhanceImage: !needProcess ? enhanceImage : true,
              width: artworkWidth,
              height: artworkHeight,
              originalWidth: selectedMetadata?.widthInch,
              originalHeight: selectedMetadata?.heightInch,
              originalDpi:
                !needProcess && enhanceImage
                  ? artworkCamelCased?.enhancedOriginalDpi
                  : originalMetadata?.dpi,
              sizeBasedDpi:
                !needProcess && enhanceImage
                  ? artworkCamelCased?.enhancedOriginalDpi
                  : originalMetadata?.dpi,
              ...(this.showMultiSize && {
                sizesAndQuantities: [
                  {
                    width: artworkWidth,
                    height: artworkHeight,
                    quantity: this.initialValues.quantity,
                    preset: "custom",
                    id: getRandomId()
                  }
                ]
              }),
              metadata: {
                ...artwork.metadata,
                widthInch: artworkWidth,
                heightInch: artworkHeight,
                aspectRatio: (
                  parseFloat(selectedMetadata.width) / parseFloat(selectedMetadata.height)
                ).toPrecision(6)
              },
              lengthFoot: artwork.metadata.length_foot,
              isLoading: false,
              isFile: false,
              enhancementCompleted: !needProcess,
              removeBackground: artwork.metadata.remove_background,
              originalImageUrl: artwork.original_image_url,
              enhancedNoBgUrl: artwork.no_bg_url,
              enhancedWithBgUrl: artwork.with_bg_url,
              sliderPreviewUrl: artwork.no_bg_no_trim_url,
              enhancedDpi: 300,
              enhancedOriginalDpi: artworkCamelCased?.enhancedOriginalDpi
            };

            this.updateArtworkValuesById(artworkValues);

            const index = findIndexById(this.artworks, artwork.id);
            const updatedArtwork = this.artworks[index];

            if (!needProcess && enhanceImage) {
              this.updatePreviewDpi({ artworkId: updatedArtwork.id });
            }
            if (needProcess) {
              this.schedulePollingFallback(artwork.id);
            }
            await this.downscaleOriginalImage({ artworkId: updatedArtwork.id });
          }
        }
      });

      await Promise.all(fetchArtworkPromises);

      this.getArtworksPricing();
      this.updatePdpDates();
    },
    /* eslint-enable max-lines-per-function */
    setGangsheetValidationError({ index, artwork, type }) {
      const { metadata, id } = artwork;
      submitDtfEvent("invalid_gang_sheet", {
        artwork_id: id,
        ...metadata
      });
      // We only need to show the first gang sheet dimension error
      this.setValidationErrorMessage({
        index,
        message: metadata.errors[0],
        type
      });
    },
    removeArtworkWithActivity(artworkId) {
      const { pdpType, artworks, businessLine } = this;

      const artworkIndex = artworks.findIndex(artwork => artwork.id === artworkId);
      if (artworkIndex === -1) return;

      const artworkToRemove = this.artworks[artworkIndex];

      this.removePresetMockupColor(artworkIndex);

      const eventContext = {
        file_name: artworkToRemove.name,
        file_type: artworkToRemove.filetype,
        file_size: artworkToRemove.filesize,
        width: artworkToRemove.metadata?.width,
        height: artworkToRemove.metadata?.height,
        page: artworkToRemove.metadata?.uploadedOn || "pdp",
        pdp_type: pdpType,
        business_line: businessLine,
        dpi: artworkToRemove.metadata?.upscalingEnabled
          ? Number(artworkToRemove.metadata?.qualityUpscaleDpi)
          : 200,
        resolution_width:
          artworkToRemove.metadata?.trimInfo?.originalWidthPx || artworkToRemove.metadata?.width,
        resolution_height:
          artworkToRemove.metadata?.trimInfo?.originalHeightPx || artworkToRemove.metadata?.height
      };
      submitDtfEvent("image_removed", eventContext);
      trackRudderstackImageRemoved({
        business_line: businessLine,
        file_name: eventContext.file_name,
        file_type: eventContext.file_type,
        file_size: eventContext.file_size,
        location: eventContext.page,
        dpi: artworkToRemove.metadata?.dpi,
        artworks_count: this.artworks?.length,
        pre_cut_transfers: this.preCut
      });

      this.removeArtwork(artworkId);
      this.setSimulation({ ...DEFAULT_SIMULATION_ARTWORK, ...this.initialValues });
    },
    trackImageUploadStart(file, isDropEvent) {
      const { pdpType, artworks, businessLine, preCut, catalogNumber } = this;
      const uploadUuid = crypto.randomUUID();

      submitDtfEvent("image_upload_started", {
        page: "pdp",
        pdp_type: pdpType,
        type: isDropEvent ? "drop" : "browse",
        file_name: file.file.name,
        file_type: file.file.type,
        file_size: file.file.size,
        dpi: 300,
        project_id: null,
        multi_artwork: artworks?.length > 1,
        artworks_count: artworks?.length,
        business_line: businessLine,
        catalog_number: catalogNumber,
        upload_uuid: uploadUuid
      });

      trackRudderstackImageUploadStarted({
        catalog_number: catalogNumber,
        location: "pdp",
        file_name: file.file.name,
        file_type: file.file.type,
        file_size: file.file.size,
        dpi: 300,
        business_line: businessLine,
        artworks_count: artworks?.length,
        pre_cut_transfers: preCut,
        upload_uuid: uploadUuid
      });

      return uploadUuid;
    },
    trackImageUploadCompleted({ index, ...data }) {
      const { artwork, file, response, uploadStartTime, uploadUuid } = data;
      const { pdpType, artworks, businessLine, preCut } = this;

      submitCompletedEvent({
        multiArtwork: artworks?.length > 1,
        artworksCount: artworks?.length,
        catalogNumber: this.catalogNumber,
        dpi: 300,
        uploadedOn: "pdp",
        preCutTransfers: preCut,
        uploadStartTime,
        file,
        artwork,
        pdpType,
        response,
        businessLine,
        uploadUuid
      });

      sendDataLayer({
        genericEvent: true,
        event: "ImageUploadCompleted",
        filetype: artwork?.filetype,
        filesize: artwork?.filesize
      });
    },
    /* eslint-disable max-lines-per-function */
    async getArtworkHistory(payload) {
      const { search, sortOrder, artworkIds, currentType, apiType, artworksType } = payload;
      try {
        const {
          artworkHistory,
          dtfChannelUuid,
          catalogNumber,
          artworkHistoryPagination: pagination
        } = this;

        const apiParams = {
          per_page: pagination.perPage,
          page: pagination.page,
          tab: artworksType,
          sort: `created_at_${sortOrder}`,
          artwork_ids: artworkIds,
          search,
          dtf_channel_uuid: dtfChannelUuid,
          catalog_number: catalogNumber
        };

        let response;

        if (apiType === "list") {
          response = await api.dtf.getArtworkHistories(apiParams);
        } else {
          response = await api.dtf.getArtworkHistory(apiParams);
        }

        response = objNamesToCamelCase(response);

        const { ...apiPageInfo } = response;
        let { artworks } = response;

        if (this.isGoldFoil && currentType === "true") {
          artworks = await Promise.all(
            artworks.map(async artwork => {
              const coloredImage = await convertImageToHexColor(
                artwork.ordered.imageUrl,
                GOLD_FOIL_HEX
              );

              artwork.ordered.goldFoilImage = coloredImage;
              artwork.ordered.imageUrl = coloredImage;

              return artwork;
            })
          );
        }

        const newArtworkList = apiPageInfo.page === 1 ? artworks : [...artworkHistory, ...artworks];

        this.setArtworkHistoryPagination(apiPageInfo);
        this.setArtworkHistory(newArtworkList);
      } catch (error) {
        window.Honeybadger?.notify("DTF previous purchase modal error", {
          params: { error_message: error.message }
        });
      }
    },

    async getLibraryCollection({ search, sortOrder, artworksType }) {
      try {
        const { artworkHistory, artworkHistoryPagination: pagination } = this;

        const apiParams = {
          q: search,
          per_page: pagination.perPage,
          page: pagination.page,
          tab: artworksType,
          sort: `saved_${sortOrder}`
        };

        const response = await api.digitalAssets.savedAssets(apiParams);

        const { assets, ...apiPageInfo } = objNamesToCamelCase(response);

        const newArtworkList = apiPageInfo.page === 1 ? assets : [...artworkHistory, ...assets];

        this.setArtworkHistoryPagination(apiPageInfo);
        this.setArtworkHistory(newArtworkList);
      } catch (error) {
        window.Honeybadger?.notify("Getting Library Collection into Modal modal error", {
          params: { error_message: error.message }
        });
      }
    },
    async changePuffColor({ color, index = 0 }) {
      const [artwork] = this.artworks;
      this.updateArtworkValues({ index, color });

      let { url } = artwork;
      if (this.isStandardArtwork) {
        url = artwork.removeBackground ? artwork.sliderPreviewUrl : artwork.enhancedWithBgUrl;
      }

      if (!url) return;

      try {
        this.setPuff({ isLoading: true });

        const coloredImage = await convertImageToHexColor(url, color);

        this.setPuff({ image: coloredImage });
      } catch (_error) {
        this.setPuff({ image: null });
      } finally {
        this.setPuff({ isLoading: false });
      }
    },
    async convertImageToHexColor({ artwork }) {
      if (artwork.url) {
        const coloredImage = await convertImageToHexColor(artwork.url, GOLD_FOIL_HEX);
        artwork.url = coloredImage;
      }

      this.setLoadedArtwork({ artwork });
    },
    async generateGoldFoilImages({ artworkId }) {
      const index = findIndexById(this.artworks, artworkId);
      const artwork = this.artworks[index];

      this.updateArtworkValuesById({
        id: artworkId,
        enhancedWithBgUrl: await convertImageToHexColor(artwork.enhancedWithBgUrl, GOLD_FOIL_HEX),
        enhancedNoBgUrl: await convertImageToHexColor(artwork.enhancedNoBgUrl, GOLD_FOIL_HEX),
        sliderPreviewUrl: await convertImageToHexColor(artwork.sliderPreviewUrl, GOLD_FOIL_HEX),
        enhancementCompleted: true
      });
    },
    updatePrintReadyInfoState(artworkId) {
      const { pdpType, isSpangle, artworks } = this;

      const artwork = artworks.find(({ id }) => String(id) === String(artworkId));
      const show =
        pdpType !== "gang_sheets" &&
        !isSpangle &&
        artwork.metadata &&
        !artwork.metadata.canRemoveBg &&
        !artwork.metadata.upscaleInfo;
      this.updateArtworkValuesById({
        id: artworkId,
        printReadyInfo: {
          show,
          expanded: false
        }
      });
    },
    updateJerseyNumberPdpDates: debounce(function updateJerseyNumberPdpDates() {
      if (!this.isValidJerseyArtwork) return;

      this.updatePdpDates();
    }, 800),
    // eslint-disable-next-line max-lines-per-function
    async downloadLettersPreview(simulation = false) {
      try {
        const { style, font, height, maxLength, color, texts, textIndex } =
          this.letterNumberArtwork;
        let text = "ABCD";
        if (texts.length) {
          const index = textIndex;
          text = texts[index];
        }
        const imageIndex = textIndex;
        let letterConfig = {
          font,
          color,
          style,
          letter_height_inch: height,
          text,
          max_length_inch: maxLength
        };

        if (simulation) {
          const presentValues = Object.fromEntries(
            Object.entries(letterConfig).filter(([_, value]) => value !== null)
          );
          letterConfig = {
            ...LETTERS_FILL_IN_VALUES,
            ...presentValues
          };
        }

        if (!letterConfig.text) return;

        this.setLetterPreviewLoading(true);

        const response = await api.dtf.generateLettersPreview({ letter_configs: letterConfig });
        const latestImageIndex = this.letterNumberArtwork.textIndex;

        if (this.artworks[0].texts[textIndex] === text) {
          this.setLetterPreviewMessage({ textIndex, message: response.validation_message });
        }

        if (imageIndex !== latestImageIndex) return;

        const { sizes } = this.letterNumberArtwork;
        sizes[textIndex] = { width: response.width, height: response.height };

        const { url } = response;

        this.updateArtworkValues({ index: 0, url, sizes });
        if (this.isLetterReadyForOrder) {
          this.getArtworksPricing();
          this.updatePdpDates();
        }
      } catch (error) {
        this.updateArtworkValues({ index: 0, url: null });
      } finally {
        this.setLetterPreviewLoading(false);
      }
    },
    debouncedGenerateLettersPreview: debounce(
      function debouncedGenerateLettersPreview() {
        if (this.isLetterPreviewReady) {
          this.downloadLettersPreview();
        } else {
          this.downloadLettersPreview(true);
        }
      },
      800,
      { leading: true }
    ),
    generateLettersPreview(useDebounce = true) {
      if (useDebounce) {
        this.debouncedGenerateLettersPreview();
      } else if (this.isLetterPreviewReady) {
        this.downloadLettersPreview();
      } else {
        this.downloadLettersPreview(true);
      }
    },
    updateAllLetterPreviews: debounce(
      function updateAllLetterPreviews() {
        if (this.isLetterReadyForOrder) {
          this.setLetterPreviewLoading(true);
          this.updateLettersPreviews();
        } else if (!this.isLetterPreviewReady) {
          this.downloadLettersPreview(true);
        }
      },
      800,
      { leading: true }
    ),
    async updateLettersPreviews() {
      try {
        const requestData = { artworks: this.formArtworks };
        const response = await api.dtf.generateLettersSizes(requestData);
        const artwork = {
          sizes: response.reduce((acc, value, index) => {
            acc[index] = value;
            return acc;
          }, {})
        };

        const { textIndex } = this.letterNumberArtwork;
        if (textIndex !== null) {
          artwork.url = artwork.sizes[textIndex].url;
        }
        this.updateArtworkValues({ index: 0, ...artwork });
        await this.getArtworksPricing();
        this.updatePdpDates();
      } catch (error) {
        window.Honeybadger?.notify("Error generating letters preview", {
          params: { error: JSON.stringify(error) }
        });
      }
    },
    async buyAsStickerById({ artworkIds }) {
      const { catalogNumber, dtfChannelUuid } = this;

      const artworks = await Promise.all(
        artworkIds.map(id =>
          api.dtf.getArtwork(
            { id },
            {
              catalog_number: catalogNumber,
              dtf_channel_uuid: dtfChannelUuid
            }
          )
        )
      );

      this.downloadAndTriggerArtworkUploadFlow(
        artworks.map(artwork => ({
          id: artwork.id,
          name: artwork.name,
          originalImageUrl: artwork.url,
          originalMimeType: artwork.filetype
        }))
      );
    },
    // eslint-disable-next-line max-lines-per-function
    async updateEnableEnhancement({ artworkId, enableEnhancement, enhancementTier }) {
      try {
        const artwork = this.artworks.find(({ id }) => String(id) === String(artworkId));
        const initialMetadata = artwork.metadata;
        const { originalMetadata, metadata, enhancedMetadata, withBgMetadata } = artwork;

        let selectedMetadata = originalMetadata;
        if (enableEnhancement && enhancementTier !== "custom") {
          selectedMetadata =
            artwork.removeBackground || enhancementTier === "full_enhancements"
              ? enhancedMetadata || metadata
              : withBgMetadata;
        }

        if (artwork.pricing?.enhancementTier) {
          this.cachedArtworkPrices = {
            ...this.cachedArtworkPrices,
            [artwork.pricing.enhancementTier]: artwork.pricing
          };
        }

        const attributes = {
          id: artworkId,
          enhanceImage: enableEnhancement,
          enhancementTier,
          width: selectedMetadata?.widthInch,
          height: selectedMetadata?.heightInch,
          originalWidth: selectedMetadata?.widthInch,
          originalHeight: selectedMetadata?.heightInch,
          originalDpi: enableEnhancement ? artwork?.enhancedOriginalDpi : originalMetadata?.dpi,
          sizeBasedDpi: enableEnhancement ? null : originalMetadata?.dpi,
          pricing: this.cachedArtworkPrices[enhancementTier] || {},
          metadata: {
            ...initialMetadata,
            aspectRatio: (
              parseFloat(selectedMetadata?.width) / parseFloat(selectedMetadata?.height)
            ).toPrecision(6)
          }
        };

        this.updateArtworkValuesById(attributes);

        if (enableEnhancement) {
          this.updatePreviewDpi({ artworkId });
        }

        await this.downscaleOriginalImage({ artworkId });

        // currently only support for first artwork
        const apiParams = { dtf_artwork: { enhance_image: enableEnhancement } };
        await api.dtf.updateArtworkRedrawData({ id: artworkId }, apiParams);

        this.getArtworksPricing();
        this.updatePdpDates();

        submitDtfEvent("dtf_image_print_perfect_toggle", {
          business_line: this.businessLine,
          artwork_id: artworkId,
          enhance_image: enableEnhancement,
          selected_by: "customer",
          metadata: enableEnhancement ? metadata : originalMetadata
        });

        submitDtfEvent("dtf_enhancement_tier_change", {
          business_line: this.businessLine,
          name: enhancementTier,
          artwork_id: artworkId,
          enhance_image: enableEnhancement,
          metadata: enableEnhancement ? metadata : originalMetadata
        });
      } catch (error) {
        // NOTES: [AR-1578] for debugging an issue. Clean up after the error is identified.
        let errorMessage = "Unknown error occurred";
        let errorStack = "No stack trace available";
        let errorType = "unknown";
        const errorDetails = {};

        if (error) {
          errorMessage = error.message || error.error || String(error);
          errorStack = error.stack || "No stack trace available";
          errorType = error.name || error.constructor?.name || typeof error;

          if (error.response) {
            errorDetails.response = {
              status: error.response.status,
              data: error.response.data
            };
          }
        }

        window.Honeybadger?.notify("Artwork enhancement setting save from PDP failed", {
          tags: "enhancement-settings-failed-to-save-pdp",
          params: {
            error_message: errorMessage,
            error_type: errorType,
            stack_trace: errorStack,
            error_details: errorDetails,
            enhance_image: enableEnhancement,
            artwork_id: artworkId,
            enhancement_tier: enhancementTier
          }
        });
      }
    },
    updatePreviewDpi({ artworkId }) {
      if (!this.isStandardArtwork) return;

      const index = findIndexById(this.artworks, artworkId);
      const artwork = this.artworks[index];

      // forces SVG images to have a 300 DPI on display
      if (artwork?.originalMime === "image/svg+xml") {
        this.updateArtworkValuesById({ id: artworkId, originalDpi: 300, sizeBasedDpi: 300 });
        return;
      }

      // logic to retrieve size
      let requestedWidth = artwork.width;
      let requestedHeight = artwork.height;
      if (artwork.sizesAndQuantities.length > 0) {
        const largestSize = artwork.sizesAndQuantities
          .filter(size => size.quantity > 0)
          .reduce(
            (largest, current) => {
              const largestDimension = parseFloat(largest.width);
              const currentDimension = parseFloat(current.width);
              return currentDimension > largestDimension ? current : largest;
            },
            artwork.sizesAndQuantities.find(size => size.quantity > 0) ||
              artwork.sizesAndQuantities[0]
          );

        requestedWidth = largestSize.width;
        requestedHeight = largestSize.height;
      }

      let resultingDpi = artwork.originalDpi;

      if (artwork.originalWidth !== requestedWidth || artwork.originalHeight !== requestedHeight) {
        resultingDpi = artwork.originalDpi / (requestedWidth / artwork.originalWidth);
      }

      if (
        !Number.isNaN(artwork.sizeBasedDpi) &&
        !Number.isNaN(resultingDpi) &&
        artwork.sizeBasedDpi !== resultingDpi
      ) {
        this.updateArtworkValuesById({ id: artworkId, sizeBasedDpi: resultingDpi });
      }
    },
    // eslint-disable-next-line max-lines-per-function
    async downscaleOriginalImage({ artworkId }) {
      if (!this.isStandardArtwork) return;

      const index = findIndexById(this.artworks, artworkId);
      const artwork = this.artworks[index];

      if (
        artwork?.metadata?.mimeType === "image/pdf" ||
        artwork?.originalImageUrl === null ||
        artwork?.originalImageUrl === undefined ||
        artwork?.filetype === "image/svg+xml" ||
        artwork?.originalMime === "image/svg+xml" ||
        !artwork?.originalImageUrl?.startsWith("http")
      ) {
        return;
      }

      const sourceImage = await this.downloadImage(artwork.originalImageUrl);

      if (
        !sourceImage.width ||
        !sourceImage.height ||
        sourceImage.width === 0 ||
        sourceImage.height === 0
      ) {
        logUploadError({
          error_message: `Invalid image dimensions: ${sourceImage.width}x${sourceImage.height}`,
          error: "Canvas rendering error",
          uploadError: "Image has invalid dimensions",
          errorResponse: {
            imageUrl: artwork.originalImageUrl,
            width: sourceImage.width,
            height: sourceImage.height
          }
        });
        return;
      }

      const originalDpi = 300; // baseline DPI
      // Fallback to originalDpi if sizeBasedDpi is invalid to prevent NaN in qualityRatio calculation
      // which would lead to 0x0 intermediate canvas dimensions and throw an error
      const targetDpi = Number.isFinite(artwork.sizeBasedDpi) ? artwork.sizeBasedDpi : originalDpi;

      if (targetDpi >= originalDpi) {
        this.updateArtworkValuesById({
          id: artworkId,
          downscaledOriginalImage: artwork.originalImageUrl
        });

        return;
      }

      const aspectRatio = sourceImage.width / sourceImage.height;
      const previewCanvasWidth = this.previewCanvasSize.width;

      if (!previewCanvasWidth || previewCanvasWidth === 0) {
        return;
      }

      if (!Number.isFinite(aspectRatio) || aspectRatio === 0) {
        logUploadError({
          error_message: `Invalid aspect ratio: aspectRatio=${aspectRatio}`,
          error: "Canvas rendering error",
          uploadError: "Invalid aspect ratio",
          errorResponse: {
            aspectRatio,
            imageWidth: sourceImage.width,
            imageHeight: sourceImage.height
          }
        });
        return;
      }

      const previewCanvasHeight = previewCanvasWidth / aspectRatio;

      try {
        // Collect diagnostics locally and attach to thrown errors only
        const debugInfo = {
          artworkId,
          previewCanvasWidth,
          previewCanvasHeight,
          aspectRatio,
          targetDpi,
          originalDpi,
          previewCanvasSize: this.previewCanvasSize,
          ab_transfers_preview_blur: this.experiments.ab_transfers_preview_blur,
          catalogNumber: this.catalogNumber,
          sizeBasedDpi: artwork.sizeBasedDpi
        };

        const resultCanvas = document.createElement("canvas");
        const resultContext = resultCanvas.getContext("2d");

        // Invert the ratio to create more noticeable blur at lower DPIs
        let qualityRatio = Math.max(0.1, targetDpi / originalDpi); // minimum 0.1 to prevent extreme blur
        debugInfo.qualityRatio = qualityRatio;

        if (this.catalogNumber === "DTF001") {
          if (this.experiments.ab_transfers_preview_blur === 1) {
            qualityRatio = Math.max(0.6, targetDpi / originalDpi);
          } else if (this.experiments.ab_transfers_preview_blur === 2) {
            qualityRatio = Math.max(0.8, targetDpi / originalDpi);
          }
        }

        // Create an intermediate canvas at a smaller size to simulate lower DPI
        const intermediateCanvas = document.createElement("canvas");
        const intermediateContext = intermediateCanvas.getContext("2d");

        // Calculate dimensions that simulate the target DPI
        const intermediateWidth = Math.max(1, Math.round(previewCanvasWidth * qualityRatio));
        const intermediateHeight = Math.max(1, Math.round(previewCanvasHeight * qualityRatio));
        debugInfo.intermediateWidth = intermediateWidth;
        debugInfo.intermediateHeight = intermediateHeight;

        // Set intermediate canvas to smaller dimensions
        intermediateCanvas.width = intermediateWidth;
        intermediateCanvas.height = intermediateHeight;

        // Validate intermediate canvas dimensions before drawing
        if (intermediateCanvas.width === 0 || intermediateCanvas.height === 0) {
          const err = new Error(
            `Intermediate canvas has invalid dimensions: ${intermediateCanvas.width}x${intermediateCanvas.height}`
          );
          err.debug = {
            ...debugInfo,
            throwAt: "intermediate_canvas_zero_dimension",
            assignedWidth: intermediateCanvas.width,
            assignedHeight: intermediateCanvas.height
          };
          throw err;
        }

        // First draw to smaller size (simulating lower DPI)
        intermediateContext.imageSmoothingEnabled = true;
        intermediateContext.imageSmoothingQuality = "low";
        intermediateContext.drawImage(sourceImage, 0, 0, intermediateWidth, intermediateHeight);

        // Calculate dimensions that simulate the target DPI
        const resultWidth = Math.max(1, previewCanvasWidth);
        const resultHeight = Math.max(1, previewCanvasHeight);
        debugInfo.resultWidth = resultWidth;
        debugInfo.resultHeight = resultHeight;

        // Set final canvas size
        resultCanvas.width = resultWidth;
        resultCanvas.height = resultHeight;

        // Validate result canvas dimensions before drawing
        if (resultCanvas.width === 0 || resultCanvas.height === 0) {
          const err = new Error(
            `Result canvas has invalid dimensions: ${resultCanvas.width}x${resultCanvas.height}`
          );
          err.debug = {
            ...debugInfo,
            throwAt: "result_canvas_zero_dimension",
            assignedWidth: resultCanvas.width,
            assignedHeight: resultCanvas.height
          };
          throw err;
        }

        // Scale back up to preview size, which will show the quality loss
        resultContext.imageSmoothingEnabled = true;
        resultContext.imageSmoothingQuality = "high";
        resultContext.drawImage(intermediateCanvas, 0, 0, resultWidth, resultHeight);

        const result = resultCanvas.toDataURL("image/png");

        if (result !== "data:,") {
          this.updateArtworkValuesById({ id: artworkId, downscaledOriginalImage: result });
        }
      } catch (error) {
        const dbg = error?.debug;
        logUploadError({
          error_message: error.message,
          error: "Canvas rendering error",
          uploadError: "Failed to downscale image",
          errorResponse: {
            imageUrl: artwork.originalImageUrl,
            imageWidth: sourceImage.width,
            imageHeight: sourceImage.height,
            previewCanvasWidth,
            previewCanvasHeight,
            aspectRatio,
            targetDpi,
            originalDpi,
            // Diagnostics if available
            qualityRatio: dbg?.qualityRatio,
            intermediateWidth: dbg?.intermediateWidth,
            intermediateHeight: dbg?.intermediateHeight,
            resultWidth: dbg?.resultWidth,
            resultHeight: dbg?.resultHeight,
            assignedWidth: dbg?.assignedWidth,
            assignedHeight: dbg?.assignedHeight,
            throwAt: dbg?.throwAt,
            previewCanvasSize: dbg?.previewCanvasSize ?? this.previewCanvasSize,
            ab_transfers_preview_blur:
              dbg?.ab_transfers_preview_blur ?? this.experiments.ab_transfers_preview_blur,
            catalogNumber: dbg?.catalogNumber ?? this.catalogNumber,
            artworkId: dbg?.artworkId ?? artworkId
          }
        });
      }
    },
    async downloadImage(url, maxRetries = 5) {
      const delays = [250, 500, 1000, 2000, 4000];
      const retryableStatuses = [408, 429, 500, 502, 503, 504];
      let blobUrl = null;

      /* eslint-disable no-await-in-loop */
      for (let attempt = 0; attempt <= maxRetries; attempt += 1) {
        try {
          const response = await fetch(url);

          if (!response.ok) {
            const error = new Error(
              `Failed to fetch image: HTTP ${response.status} ${response.statusText} for ${url}`
            );
            error.status = response.status;
            throw error;
          }

          const blob = await response.blob();
          blobUrl = URL.createObjectURL(blob);
          break;
        } catch (error) {
          if (blobUrl) {
            URL.revokeObjectURL(blobUrl);
          }

          const isRetryable = error?.status && retryableStatuses.includes(error.status);
          const lastAttempt = attempt === maxRetries;
          if (lastAttempt || !isRetryable) {
            throw error;
          }

          const delay = delays[Math.min(attempt, delays.length - 1)];
          await new Promise(resolve => {
            setTimeout(resolve, delay);
          });
        }
      }
      /* eslint-enable no-await-in-loop */

      const image = new Image();

      await new Promise((resolve, reject) => {
        image.onload = () => {
          URL.revokeObjectURL(blobUrl);
          resolve();
        };
        image.onerror = event => {
          URL.revokeObjectURL(blobUrl);
          const error = new Error("Failed to decode image");
          error.event = event;
          reject(error);
        };
        image.src = blobUrl;
      });

      return image;
    },
    fetchBulkPrice() {
      Object.entries(this.gangSheetBuilderVariants).forEach(async ([_, value]) => {
        const response = await api.dtf.fetchBulkPrice({ variant_id: value.variant_id });
        const bulkPrice = {};

        bulkPrice[value.variant_id] = response;

        this.setGangSheetBulkPrice(bulkPrice);
        this.updateBulkTable();
      });
    },
    updateBulkTable() {
      if (!this.gangSheetBulkPrice[this.gangSheetBuilderSettings.variantId]) return;

      EventBus.publish(Events.BULK_TABLE_UPDATE, {
        htmlTable: this.gangSheetBulkPrice[this.gangSheetBuilderSettings.variantId].table
      });
    },
    updateCarouselImage({ variantId }) {
      EventBus.publish(Events.SELECTED_VARIANT_CHANGED, {
        carouselData: this.gangSheetBuilderCarousels[variantId]
      });
    },
    sanitizeObject(obj) {
      return Object.fromEntries(Object.entries(obj).filter(([_, value]) => value != null));
    },
    async cancelArtworkProcess(artwork) {
      if (!artwork.id) return;

      api.dtf.cancelArtworkProcess({ id: artwork.id });
    },
    sendCancelEvent(artwork) {
      const { lastWebSocketEvent, id } = artwork;
      let action;

      if (lastWebSocketEvent !== null) {
        if (lastWebSocketEvent === "fetch_delivery_date") {
          action = "spangle_api";
        } else if (lastWebSocketEvent === "upload_artwork") {
          action = "image_upload";
        } else {
          action = "image_processing";
        }
      } else if (this.isSpangle) {
        action = "spangle_api";
      } else {
        action = "image_upload";
      }

      prometheusTracker.trackImageProcessingCancelled(this.catalogNumber, action);

      submitDtfEvent("transfers_cancel_button_clicked", {
        action,
        catalog_number: this.catalogNumber,
        business_line: this.businessLine,
        artwork_id: id
      });
    },
    handleCancelProcess(artwork) {
      const spanglesStore = useSpanglesStore();
      this.updateArtworkValues({ index: artwork.originalIndex, processTimeStamp: Date.now() });
      this.sendCancelEvent(artwork);
      this.cancelArtworkProcess(artwork);
      this.clearValidateArtworkStartEventInterval(artwork.id);

      if (artwork.id) {
        transfersPollingService.stopPolling(artwork.id);
      }

      if (
        !artwork.isLoading &&
        spanglesStore.spangleVariations.some(variation => variation.spanglePagesInfo !== null)
      ) {
        spanglesStore.cancelSpangleProcess({ artwork });
      } else if (artwork.id) {
        this.removeArtwork(artwork.id);
      } else {
        this.removeArtworkByIndex(artwork.originalIndex);
      }

      // Recalculate pricing and enhancements after removing artwork
      this.getArtworksPricing();
    },

    defineOriginalWidth(ordered) {
      if (ordered.enhanceImage) {
        return ordered.removeBackground
          ? ordered.enhancedMetadata?.widthInch
          : ordered.withBgMetadata?.widthInch;
      }

      return ordered.originalMetadata?.widthInch;
    },
    defineOriginalHeight(ordered) {
      if (ordered.enhanceImage) {
        return ordered.removeBackground
          ? ordered.enhancedMetadata?.heightInch
          : ordered.withBgMetadata?.heightInch;
      }

      return ordered.originalMetadata?.heightInch;
    },
    async loadEnhancementArtwork({
      enhancementsStarted,
      dtfChannelUuid,
      rawArtworkUrls,
      artworkWidth,
      artworkHeight
    }) {
      if (!enhancementsStarted || !dtfChannelUuid || !rawArtworkUrls) {
        return;
      }

      this.isEnhancementLoading = true;

      try {
        const { parseArtworkUrls } = await import("common/utils/json");
        const parsedUrls = parseArtworkUrls(rawArtworkUrls);
        if (!parsedUrls?.artworkIds?.length) {
          return;
        }

        const { artworkIds, lastArtworkId } = parsedUrls;

        // Initialize loading state
        this.initializeLoadingState(lastArtworkId);

        // Fetch artwork details
        const artworkDetails = await this.fetchArtworkDetails(artworkIds, dtfChannelUuid);

        if (!artworkDetails?.artworks?.length) {
          return;
        }

        const artwork = artworkDetails.artworks.map(
          ({
            id = null,
            fileName = "",
            originalMime = "",
            enhancementTier = "",
            uploadedOn = "",
            purchasedDate = "",
            ordered = {},
            original = {},
            useFootLength,
            dtfPrintId,
            currentEnhancementState,
            sizesAndQuantities
          }) => ({
            id,
            dtfPrintId,
            name: fileName,
            uploadedOn,
            purchasedDate,
            originalMime,
            enhancementTier,
            url: ordered.imageUrl,
            spangledImageUrl: ordered?.spangledImageUrl,
            cutlineUrl: ordered?.cutlineUrl,
            originalImageUrl: original.imageUrl,
            originalMimeType: original.mimeType,
            cmykPreviewImageNoBackgroundUrl: ordered?.cmykPreviewImageNoBackgroundUrl,
            cmykPreviewImageWithBackgroundUrl: ordered?.cmykPreviewImageWithBackgroundUrl,
            processTimeStamp: Date.now(),
            goildFoilPreviewAttached: this.isGoldFoil,
            enhancedNoBgUrl: ordered.noBgUrl,
            enhancedWithBgUrl: ordered.withBgUrl,
            enhancementCompleted: true,
            originalDpi: ordered.dpi,
            enhancedDpi: ordered.dpi,
            sliderPreviewUrl: ordered.noBgNoTrimUrl,
            canRemoveBg: ordered.canRemoveBg || false,
            useFootLength: useFootLength || false,
            removeBackground: ordered.removeBackground || false,
            metadata: ordered?.metadata,
            originalMetadata: ordered?.originalMetadata,
            enhancedMetadata: ordered?.enhancedMetadata,
            withBgMetadata: ordered?.withBgMetadata,
            enhanceImage: ordered?.enhanceImage,
            previewMetadata: this.printAsIs ? { heightInch: ordered.height } : {},
            enhancedOriginalDpi: ordered?.enhancedOriginalDpi,
            originalWidth: this.defineOriginalWidth(ordered),
            originalHeight: this.defineOriginalHeight(ordered),
            sizeBasedDpi: ordered?.enhanceImage
              ? ordered?.enhancedOriginalDpi
              : ordered?.originalMetadata?.dpi,
            currentEnhancementState,
            sizesAndQuantities
          })
        )[0];

        const isEnhancementComplete = artwork.currentEnhancementState === "complete_processing";

        if (isEnhancementComplete) {
          await this.setupCompletedEnhancement(artwork);
        } else {
          await this.setupInProgressEnhancement(artwork, dtfChannelUuid, {
            width: artworkWidth,
            height: artworkHeight
          });
        }

        this.cleanupUrlParameters();
      } catch (error) {
        window.Honeybadger?.notify("Enhancement loading failed", {
          params: { error: error.message }
        });
      } finally {
        this.isEnhancementLoading = false;
      }
    },

    initializeLoadingState(artworkId) {
      this.artworksState = [
        {
          ...emptyArtworkItem,
          id: artworkId,
          isLoading: true,
          isFile: false,
          originalIndex: 0
        }
      ];

      this.simulation = { ...DEFAULT_SIMULATION_ARTWORK };

      this.updateArtworkValuesById({
        id: artworkId,
        isLoading: false,
        enhancementCompleted: false,
        imageProcessingCompletedEventSent: false,
        progress: 50,
        progressMessage: "Loading artwork details...",
        processTimeStamp: Date.now()
      });
    },

    async fetchArtworkDetails(artworkIds, dtfChannelUuid) {
      this.updateArtworkValuesById({
        id: artworkIds[artworkIds.length - 1],
        progress: 50,
        currentProgress: 25,
        progressMessage: "Loading artwork details..."
      });

      const responseHistory = await Promise.all([
        api.dtf.getArtworkHistory({
          artwork_ids: artworkIds,
          dtf_channel_uuid: dtfChannelUuid,
          catalog_number: this.catalogNumber,
          enhancement_started: true
        }),
        api.dtf.getArtwork(
          {
            id: artworkIds[artworkIds.length - 1]
          },
          {
            catalog_number: this.catalogNumber,
            dtf_channel_uuid: dtfChannelUuid
          }
        )
      ]);

      const artworks = responseHistory[0].artworks.map(item => ({
        ...item,
        ordered: { ...item.ordered, ...responseHistory[1] }
      }));

      return objNamesToCamelCase({ ...responseHistory[0], artworks });
    },

    async setupCompletedEnhancement(artwork) {
      const artworkWithCompletion = {
        ...artwork,
        enhancementCompleted: true
      };

      this.setLoadedArtwork({ artwork: artworkWithCompletion });

      this.updateArtworkValuesById({
        id: artwork.id,
        progress: 100,
        progressMessage: "Enhancement complete",
        processTimeStamp: Date.now(),
        isLoading: false,
        enhancementCompleted: true
      });

      if (this.isStandardArtwork) {
        this.updatePreviewDpi({ artworkId: artwork.id });
        await this.downscaleOriginalImage({ artworkId: artwork.id });
      }

      this.updateArtworkValuesById({
        id: artwork.id,
        imageProcessingCompletedEventSent: true
      });

      this.updatePrintReadyInfoState(artwork.id);
    },

    async setupInProgressEnhancement(artwork, dtfChannelUuid, dimensions) {
      this.setDtfChannelUuid(dtfChannelUuid);
      const cable = initCable(this);
      this.setCable(cable);
      const { width: artworkWidth, height: artworkHeight } = dimensions;

      this.updateArtworkValuesById({
        id: artwork.id,
        isLoading: false,
        enhancementCompleted: false,
        progress: 50,
        progressMessage: "Processing enhancements...",
        processTimeStamp: Date.now(),
        url: artwork.url,
        originalImageUrl: artwork.originalImageUrl,
        canRemoveBg: artwork.canRemoveBg,
        removeBackground: artwork.removeBackground,
        filetype: artwork.filetype,
        filesize: artwork.filesize,
        name: artwork.name,
        ...this.initialValues,
        metadata: {
          ...artwork.metadata,
          width: artwork.metadata?.width ?? artworkWidth,
          height: artwork.metadata?.height ?? artworkHeight,
          widthInch: artwork.metadata?.widthInch ?? artworkWidth,
          heightInch: artwork.metadata?.heightInch ?? artworkHeight
        },
        withBgMetadata: {
          ...artwork.withBgMetadata,
          width: artwork.withBgMetadata?.width ?? artworkWidth,
          height: artwork.withBgMetadata?.height ?? artworkHeight,
          widthInch: artwork.withBgMetadata?.widthInch ?? artworkWidth,
          heightInch: artwork.withBgMetadata?.heightInch ?? artworkHeight
        }
      });

      this.schedulePollingFallback(artwork.id);
    },

    cleanupUrlParameters() {
      const newUrl = new URL(window.location);
      const paramsToDelete = [
        "uploaded_artwork_ids",
        "artwork_urls",
        "file_content_type",
        "enhancements_started",
        "dtf_channel_uuid",
        "side"
      ];

      paramsToDelete.forEach(param => newUrl.searchParams.delete(param));
      window.history.replaceState({}, "", newUrl.toString());
    }
  }
});

export default useDtfStore;
